import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Save, AlertCircle, CalendarDays, Clock, Search, Info, Inbox, Tag, Star, ChevronUp, ChevronDown } from "lucide-react";
import DeliveryDatePicker from "@/components/DeliveryDatePicker";
import { useOrderUnits } from "@/lib/useOrderUnits";
import { getSeasonalStatusOption, isHiddenFromStore } from "@/lib/seasonal";
import { sortCategories } from "@/lib/categoryOrder";
import { fuzzyMatchJa } from "@/lib/japaneseSearch";
import { themeFor } from "@/lib/labelColors";
import { OrderPreviewDialog } from "@/components/store/OrderPreviewDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// 曜日ごとの背景色 — 落ち着いた配色（控えめなニュートラル）
const DAY_BG_PRIMARY = "bg-white";
const DAY_BG_SECONDARY = "bg-slate-50";
const dayBg = (idx) => (idx % 2 === 0 ? DAY_BG_PRIMARY : DAY_BG_SECONDARY);
const dayHeaderBg = (idx) => (idx % 2 === 0 ? "bg-white" : "bg-slate-50");

// カテゴリ用カラーパレット
const CATEGORY_PALETTES = [
  { tagBg: "bg-emerald-100", tagText: "text-emerald-800", tagBorder: "border-emerald-300", row: "bg-emerald-50/30", cardBase: "bg-emerald-50/70 border-emerald-300 text-emerald-800 hover:bg-emerald-100", cardActive: "bg-emerald-600 border-emerald-700 text-white ring-1 ring-emerald-400" },
  { tagBg: "bg-rose-100", tagText: "text-rose-800", tagBorder: "border-rose-300", row: "bg-rose-50/30", cardBase: "bg-rose-50/70 border-rose-300 text-rose-800 hover:bg-rose-100", cardActive: "bg-rose-600 border-rose-700 text-white ring-1 ring-rose-400" },
  { tagBg: "bg-sky-100", tagText: "text-sky-800", tagBorder: "border-sky-300", row: "bg-sky-50/30", cardBase: "bg-sky-50/70 border-sky-300 text-sky-800 hover:bg-sky-100", cardActive: "bg-sky-600 border-sky-700 text-white ring-1 ring-sky-400" },
  { tagBg: "bg-violet-100", tagText: "text-violet-800", tagBorder: "border-violet-300", row: "bg-violet-50/30", cardBase: "bg-violet-50/70 border-violet-300 text-violet-800 hover:bg-violet-100", cardActive: "bg-violet-600 border-violet-700 text-white ring-1 ring-violet-400" },
  { tagBg: "bg-orange-100", tagText: "text-orange-800", tagBorder: "border-orange-300", row: "bg-orange-50/30", cardBase: "bg-orange-50/70 border-orange-300 text-orange-800 hover:bg-orange-100", cardActive: "bg-orange-600 border-orange-700 text-white ring-1 ring-orange-400" },
  { tagBg: "bg-teal-100", tagText: "text-teal-800", tagBorder: "border-teal-300", row: "bg-teal-50/30", cardBase: "bg-teal-50/70 border-teal-300 text-teal-800 hover:bg-teal-100", cardActive: "bg-teal-600 border-teal-700 text-white ring-1 ring-teal-400" },
  { tagBg: "bg-pink-100", tagText: "text-pink-800", tagBorder: "border-pink-300", row: "bg-pink-50/30", cardBase: "bg-pink-50/70 border-pink-300 text-pink-800 hover:bg-pink-100", cardActive: "bg-pink-600 border-pink-700 text-white ring-1 ring-pink-400" },
  { tagBg: "bg-indigo-100", tagText: "text-indigo-800", tagBorder: "border-indigo-300", row: "bg-indigo-50/30", cardBase: "bg-indigo-50/70 border-indigo-300 text-indigo-800 hover:bg-indigo-100", cardActive: "bg-indigo-600 border-indigo-700 text-white ring-1 ring-indigo-400" },
];

// 文字列から安定したインデックスを生成
const hashIndex = (str, mod) => {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % mod;
};

const getCategoryPalette = (categoryId) => {
  if (!categoryId) return null;
  return CATEGORY_PALETTES[hashIndex(categoryId, CATEGORY_PALETTES.length)];
};

// 顧客表示ラベル（管理画面で設定）: グループカード & 商品名バッジ用の配色
const LABEL_PREFIX = "label:";
// 管理画面「表示ラベル管理」から取得した動的メタ（name -> {color, sort_order}）。
// fetchData で setLabelMeta() により更新される。未取得時は既定の固定ラベルにフォールバック。
const DEFAULT_LABEL_ORDER = ["新入荷", "旬", "おすすめ", "食育", "期間限定", "人気"];
const DEFAULT_LABEL_COLORS = { "新入荷": "emerald", "旬": "rose", "おすすめ": "amber", "食育": "sky", "期間限定": "violet", "人気": "orange" };
let LABEL_META = {}; // name -> {color, sort_order}
let LABEL_ORDER = [...DEFAULT_LABEL_ORDER];
const setLabelMeta = (labels) => {
  if (!Array.isArray(labels) || labels.length === 0) {
    LABEL_META = {};
    LABEL_ORDER = [...DEFAULT_LABEL_ORDER];
    return;
  }
  const sorted = [...labels].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.name || "").localeCompare(b.name || ""));
  LABEL_META = {};
  LABEL_ORDER = [];
  sorted.forEach((l) => {
    LABEL_META[l.name] = { color: l.color || "slate", sort_order: l.sort_order || 0 };
    LABEL_ORDER.push(l.name);
  });
};
const labelStyle = (label) => {
  const meta = LABEL_META[label];
  if (meta) return themeFor(meta.color);
  const c = DEFAULT_LABEL_COLORS[label];
  if (c) return themeFor(c);
  return themeFor("slate");
};
const orderLabels = (labels) => {
  const arr = labels || [];
  const orderRef = LABEL_ORDER.length ? LABEL_ORDER : DEFAULT_LABEL_ORDER;
  const known = orderRef.filter((l) => arr.includes(l));
  const extra = arr.filter((l) => !orderRef.includes(l));
  return [...known, ...extra];
};
// グループ/商品の表示ラベルを取得（グループはメンバーの和集合）
const productLabels = (group) => {
  if (group?.members) {
    return orderLabels([...new Set(group.members.flatMap((m) => m.display_labels || []))]);
  }
  return orderLabels(group?.display_labels || []);
};
// 商品名の横に表示するラベルバッジ
const LabelBadges = () => {
  // 発注一覧では表示ラベル（注文の多い果物/野菜 等）のバッジは表示しない（情報過多のため）。
  // ラベルでの絞り込みは左サイドバーで可能。
  return null;
};

const getWeekday = (dateString) => {
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  return days[new Date(dateString).getDay()];
};

// 使用日を "M/D" 形式に整形（例: "2026-08-04" → "8/4"）
const fmtUsageDate = (dateString) => {
  const parts = (dateString || "").split("-");
  if (parts.length !== 3) return dateString;
  return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
};

// 同名商品で単位だけが違うケースを見分けやすくするための単位バッジ配色。
// 単位文字列ごとに安定した色を割り当て（例: kg=青系, 個=橙系）。
const UNIT_BADGE_PALETTE = [
  "bg-blue-100 text-blue-700 border-blue-300",
  "bg-orange-100 text-orange-700 border-orange-300",
  "bg-purple-100 text-purple-700 border-purple-300",
  "bg-teal-100 text-teal-700 border-teal-300",
  "bg-pink-100 text-pink-700 border-pink-300",
  "bg-cyan-100 text-cyan-700 border-cyan-300",
];
const unitBadgeClass = (unit) => {
  const u = (unit || "").toString();
  let h = 0;
  for (let i = 0; i < u.length; i++) h = (h * 31 + u.charCodeAt(i)) >>> 0;
  return UNIT_BADGE_PALETTE[h % UNIT_BADGE_PALETTE.length];
};

// kg発注のおすすめを示す控えめな「!」マーク（クリックで説明を表示）
const KgRecommendMark = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        data-testid="kg-recommend-mark"
        aria-label="kg発注のおすすめ"
        className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 text-white text-[10px] font-bold leading-none hover:bg-amber-500 transition-colors cursor-pointer"
      >
        !
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" className="w-56 p-3 text-xs text-slate-700">
      個体差があるため、<span className="font-bold text-emerald-700">kg単位での発注</span>をおすすめします。
    </PopoverContent>
  </Popover>
);



// ── 商品グループ化（同じ商品名で単位だけ違う商品を1グループにまとめる）──
// 商品名末尾の単位表記を除いた部分をグループ名として自動判定する
const _UNIT_ALT = "(?:kg|㎏|ｋｇ|KG|Kg|g|ｇ|個|[PpＰｐ]|本|袋|パック|ケース|ケース入り|束|玉|株|枚|箱|缶|房|カット|ｹｰｽ)";
const stripUnitFromName = (name) => {
  if (!name) return "";
  let s = String(name).trim();
  // 末尾の括弧付き単位:（kg）(kg)【kg】[kg]
  s = s.replace(new RegExp("[\\s　]*[（(【\\[]\\s*" + _UNIT_ALT + "\\s*[）)】\\]][\\s　]*$"), "");
  // 末尾のスペース区切り単位: "バナナ kg" / "バナナ　本"
  s = s.replace(new RegExp("[\\s　]+" + _UNIT_ALT + "[\\s　]*$"), "");
  return s.trim();
};
// メンバー商品の実効単位（unitフィールド優先、無ければ商品名から推定）
const effectiveUnit = (product) => {
  if (product?.unit) return product.unit;
  const m = String(product?.name || "").match(new RegExp("(" + _UNIT_ALT + ")[\\s　]*[）)】\\]]?$"));
  return m ? m[1] : "";
};
const isKgUnitStr = (u) => (u || "").toLowerCase().includes("kg") || (u || "").includes("㎏");
// 商品のグループキー（お気に入り等で使用。buildProductGroups と同一ロジック）
const groupKeyOf = (p) => p.group_key || (p.group_name || "").trim() || stripUnitFromName(p.name) || p.id;
// フィルタ済み商品配列 → グループ配列（表示順を維持）
const buildProductGroups = (products) => {
  const map = new Map(); // groupKey -> {groupKey, baseName, members: []}
  const order = [];
  for (const p of products) {
    const manual = (p.group_name || "").trim();
    const key = p.group_key || manual || stripUnitFromName(p.name) || p.id;
    if (!map.has(key)) {
      map.set(key, { groupKey: key, baseName: manual || stripUnitFromName(p.name) || p.name, members: [] });
      order.push(key);
    }
    map.get(key).members.push(p);
  }
  return order.map((k) => {
    const g = map.get(k);
    const units = [...new Set(g.members.map((m) => effectiveUnit(m)).filter(Boolean))];
    g.isGroup = g.members.length > 1 && units.length > 1;
    return g;
  });
};
// 価格が入っているメンバーを返す（kg優先）。単価・発注単位の表示に使用。
const priceBearingMember = (group, rank) => {
  const r = rank || "a1";
  const withPrice = group.members.filter((m) => Number(m.prices?.[r] || 0) > 0);
  if (withPrice.length === 0) {
    return group.members.find((m) => isKgUnitStr(effectiveUnit(m))) || group.members[0];
  }
  return withPrice.find((m) => isKgUnitStr(effectiveUnit(m))) || withPrice[0];
};
// グループの初期選択メンバー(4a: 既存発注のある単位→kg→先頭)
// 単位グループの初期選択: その日の注文 → 前回(直近)注文した単位 → kg → 先頭
const defaultGroupMemberId = (group, usageDate, orderData, lastOrdered = {}) => {
  const withOrder = group.members.find(
    (m) => orderData[`${m.id}_${usageDate}`]?.quantity
  );
  if (withOrder) return withOrder.id;
  const recent = group.members
    .filter((m) => lastOrdered[m.id])
    .sort((a, b) => lastOrdered[b.id].localeCompare(lastOrdered[a.id]))[0];
  if (recent) return recent.id;
  const kg = group.members.find((m) => isKgUnitStr(effectiveUnit(m)));
  if (kg) return kg.id;
  return group.members[0].id;
};

// 用途分けの表示番号: 1→①, 2→② ...
const CIRCLED_NUMS = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";
const circledNum = (n) => (n >= 1 && n <= CIRCLED_NUMS.length ? CIRCLED_NUMS[n - 1] : `(${n})`);

// メモ化された週間入力行 — グループ（同名・異単位まとめ）対応
const WeeklyRow = React.memo(function WeeklyRow({
  group,
  scheduleMeta,
  rowOrders,
  unitSel,
  rowBg,
  stickyBg: stickyBgProp,
  onQuantityChange,
  onCellDeliveryChange,
  onCellUnitChange,
  deliveryOptions,
  columnDefaults,
  customerRank,
  highlightUnit,
  kgRecommend,
  onAddSplit,
  onUpdateSplitQty,
  onUpdateSplitDelivery,
  onRemoveSplit,
  isFav,
  onToggleFav,
  showReorder,
  canUp,
  canDown,
  onMoveUp,
  onMoveDown,
}) {
  const stickyBg = stickyBgProp || "bg-white";
  const isGroup = group.isGroup;
  // 参照用メンバー（kg優先→先頭）: sticky列の単価・重量表示に使用
  const priceMember = priceBearingMember(group, customerRank);
  const price = priceMember.prices?.[customerRank] || 0;
  const displayName = isGroup ? group.baseName : group.members[0].name;
  const refProduct = group.members[0];

  // セルの選択中メンバーを取得
  const memberForCell = (usageDate) => {
    if (!isGroup) return group.members[0];
    const selId = unitSel?.[usageDate] || defaultGroupMemberId(group, usageDate, {});
    return group.members.find((m) => m.id === selId) || group.members[0];
  };

  return (
    <tr className={`hover:bg-slate-100/70 ${rowBg}`}>
      <td className={`py-1.5 px-3 align-middle text-sm sticky left-0 z-[1] w-64 border-r border-slate-200 ${stickyBg}`}>
        <div className="flex items-center justify-end gap-1.5 text-right">
          {showReorder && (
            <span className="flex flex-col shrink-0">
              <button type="button" onClick={onMoveUp} disabled={!canUp} data-testid={`fav-up-${group.groupKey}`} className="h-3 leading-none text-slate-400 hover:text-emerald-600 disabled:opacity-25"><ChevronUp className="h-3 w-3" /></button>
              <button type="button" onClick={onMoveDown} disabled={!canDown} data-testid={`fav-down-${group.groupKey}`} className="h-3 leading-none text-slate-400 hover:text-emerald-600 disabled:opacity-25"><ChevronDown className="h-3 w-3" /></button>
            </span>
          )}
          <button
            type="button"
            onClick={onToggleFav}
            data-testid={`fav-toggle-${group.groupKey}`}
            title={isFav ? "お気に入り解除" : "お気に入りに追加"}
            className="shrink-0 p-0.5 rounded hover:bg-amber-50"
          >
            <Star className={`h-4 w-4 ${isFav ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
          </button>
          <span className="font-medium truncate text-slate-800">{displayName}</span>
          {(kgRecommend || isGroup) && <KgRecommendMark />}
          <LabelBadges labels={productLabels(group)} />
          {(() => {
            const opt = getSeasonalStatusOption(refProduct.seasonal_status);
            if (opt) {
              return (
                <span className={`shrink-0 px-1 py-0 rounded-sm font-semibold text-[10px] ${opt.pillClass}`}>
                  {opt.label}
                </span>
              );
            }
            if (refProduct.is_seasonal) {
              return (
                <span className="shrink-0 px-1 py-0 rounded-sm bg-rose-500 text-white border border-rose-600 font-semibold text-[10px]">
                  季節
                </span>
              );
            }
            return null;
          })()}
        </div>
      </td>
      <td className={`py-1.5 px-2 align-middle text-xs sticky left-64 z-[1] w-14 border-r border-slate-300 text-center ${stickyBg}`}>
        {isGroup ? (
          <span className={`inline-block px-1.5 py-0 rounded font-bold text-[11px] border ${unitBadgeClass(effectiveUnit(priceMember))}`}>
            {effectiveUnit(priceMember) || "—"}
          </span>
        ) : highlightUnit && refProduct.unit ? (
          <span className={`inline-block px-1.5 py-0 rounded font-bold text-[11px] border ${unitBadgeClass(refProduct.unit)}`}>
            {refProduct.unit}
          </span>
        ) : (
          <span className="text-slate-700 font-medium">{refProduct.unit || "—"}</span>
        )}
      </td>
      <td className={`py-1.5 px-2 align-middle text-xs sticky left-[19rem] z-[1] w-24 border-r border-slate-300 text-center ${stickyBg}`}>
        <span className="text-slate-600 text-[11px]">{refProduct.estimated_weight || "—"}</span>
      </td>
      <td className={`py-1.5 px-2 align-middle text-xs sticky left-[24rem] z-[1] w-20 border-r-2 border-slate-300 text-right ${stickyBg}`}>
        <span className="text-slate-700 font-medium">{`¥${price || "—"}`}</span>
      </td>
      {scheduleMeta.map(({ schedule, locked }) => {
        const usageDate = schedule.delivery_date;
        const activeProduct = memberForCell(usageDate);
        const orderInfo = rowOrders[`${activeProduct.id}_${usageDate}`] || {
          quantity: "",
          unit: effectiveUnit(activeProduct) || "g",
        };
        const hasValue = orderInfo.quantity && parseFloat(orderInfo.quantity) > 0;
        const cellBg = hasValue ? "bg-lime-50" : "bg-emerald-50";
        if (locked) {
          return (
            <React.Fragment key={`${group.groupKey}-${schedule.id}`}>
              <td className={`py-1 px-2 align-middle border-r border-slate-100 text-center text-xs text-slate-500 ${cellBg}`}>
                {effectiveUnit(activeProduct) || "g"}
              </td>
              <td className={`py-1 px-2 align-middle text-center font-mono text-sm border-r-2 border-slate-200 ${cellBg} ${hasValue ? "text-emerald-700 font-semibold" : "text-slate-300"}`}>
                {hasValue
                  ? Number(orderInfo.quantity) % 1 === 0
                    ? Number(orderInfo.quantity).toFixed(0)
                    : Number(orderInfo.quantity).toFixed(2)
                  : "—"}
              </td>
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={`${group.groupKey}-${schedule.id}`}>
            <td className={`py-1 px-1 align-middle border-r border-slate-100 text-center text-xs text-slate-700 font-medium ${cellBg}`}>
              {isGroup ? (() => {
                const hasKg = group.members.some((m) => isKgUnitStr(effectiveUnit(m)));
                const selIsKg = isKgUnitStr(effectiveUnit(activeProduct));
                return (
                  <div className="flex flex-col items-stretch gap-0.5">
                    <select
                      value={activeProduct.id}
                      onChange={(e) => onCellUnitChange(usageDate, activeProduct.id, e.target.value)}
                      data-testid={`cell-unit-${group.groupKey}-${usageDate}`}
                      title="単位を選択（対応する商品コードで発注されます）"
                      className={`h-8 w-full text-xs rounded bg-white px-1 focus:outline-none focus:ring-1 focus:ring-emerald-400 border ${hasKg && !selIsKg ? "border-amber-400" : "border-slate-300"}`}
                    >
                      {group.members.map((m) => (
                        <option key={m.id} value={m.id}>
                          {effectiveUnit(m) || "—"}
                        </option>
                      ))}
                    </select>
                    {hasKg && !selIsKg && (
                      <span
                        data-testid={`kg-recommend-${group.groupKey}-${usageDate}`}
                        title="kg単位での発注がおすすめです"
                        className="inline-flex items-center justify-center gap-0.5 px-1 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-300 text-[9px] font-bold leading-none"
                      >
                        ⚠ kg推奨
                      </span>
                    )}
                  </div>
                );
              })() : (
                refProduct.unit || "—"
              )}
            </td>
            <td className={`py-1 px-1 align-middle border-r-2 border-slate-200 ${cellBg}`}>
              <input
                type="number"
                step="0.01"
                min="0"
                value={orderInfo.quantity}
                onChange={(e) => onQuantityChange(activeProduct.id, usageDate, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const nodeList = Array.from(
                      document.querySelectorAll(`input[data-order-date="${usageDate}"]`)
                    );
                    const idx = nodeList.indexOf(e.target);
                    if (idx >= 0 && idx < nodeList.length - 1) {
                      const next = nodeList[idx + 1];
                      next.focus();
                      if (typeof next.select === "function") next.select();
                    }
                    return;
                  }
                  if (e.key !== "ArrowUp" && e.key !== "ArrowDown" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
                  e.preventDefault();
                  const dir = e.key;
                  const nodeList = Array.from(
                    document.querySelectorAll(`input[data-order-date="${usageDate}"]`)
                  );
                  const idx = nodeList.indexOf(e.target);
                  let next = null;
                  if (dir === "ArrowUp" && idx > 0) next = nodeList[idx - 1];
                  else if (dir === "ArrowDown" && idx >= 0 && idx < nodeList.length - 1) next = nodeList[idx + 1];
                  else if (dir === "ArrowLeft" || dir === "ArrowRight") {
                    const tr = e.target.closest("tr");
                    const inputsInRow = tr ? Array.from(tr.querySelectorAll("input[type=number]")) : [];
                    const i = inputsInRow.indexOf(e.target);
                    if (dir === "ArrowLeft" && i > 0) next = inputsInRow[i - 1];
                    if (dir === "ArrowRight" && i >= 0 && i < inputsInRow.length - 1) next = inputsInRow[i + 1];
                  }
                  if (next) { next.focus(); if (typeof next.select === "function") next.select(); }
                }}
                data-order-date={usageDate}
                disabled={!!orderInfo.ep_locked}
                title={orderInfo.ep_locked ? "事前取込済みのため変更はお電話でお問い合わせください" : undefined}
                data-testid={`order-input-${activeProduct.id}-${usageDate}`}
                className={`h-9 w-full text-base border border-slate-200 hover:border-slate-300 focus:bg-white focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-400 text-center font-mono px-2 rounded-sm transition-colors disabled:bg-slate-100 disabled:text-slate-400 ${hasValue ? "bg-white font-semibold text-emerald-700 border-emerald-300" : "bg-white text-slate-400"}`}
                placeholder="—"
              />
              {hasValue && (() => {
                const colDefault = columnDefaults?.[usageDate] || "";
                const cellDelivery = orderInfo.delivery_date || colDefault;
                const isOverride = orderInfo.delivery_date && orderInfo.delivery_date !== colDefault;
                return (
                  <select
                    value={cellDelivery}
                    onChange={(e) => onCellDeliveryChange(activeProduct.id, usageDate, e.target.value)}
                    data-testid={`cell-delivery-${activeProduct.id}-${usageDate}`}
                    title="この商品の配送希望日（使用日と別の日に届けたい場合に変更）"
                    className={`mt-1 w-full text-[11px] leading-tight px-0.5 py-0.5 border rounded bg-white text-center focus:outline-none focus:ring-1 focus:ring-emerald-400 ${isOverride ? "border-amber-400 text-amber-700 font-semibold" : "border-slate-200 text-slate-600"}`}
                  >
                    <option value="">別日配送?</option>
                    {deliveryOptions?.map((o) => (
                      <option key={o.date} value={o.date}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                );
              })()}
              {(orderInfo.lines || []).length > 0 && (
                <div className="mt-1 space-y-1">
                  <div className="text-[9px] font-bold text-emerald-700 text-center leading-none">① {Number(orderInfo.quantity || 0) % 1 === 0 ? Number(orderInfo.quantity || 0).toFixed(0) : Number(orderInfo.quantity || 0).toFixed(2)}</div>
                  {orderInfo.lines.map((ln, i) => (
                    <div key={i} className="flex items-center gap-0.5" data-testid={`cell-split-${activeProduct.id}-${usageDate}-${i}`}>
                      <span className="text-[10px] font-bold text-emerald-700 w-3.5 text-center shrink-0">{circledNum(i + 2)}</span>
                      <input
                        type="number" step="0.01" min="0"
                        value={ln.quantity}
                        onChange={(e) => onUpdateSplitQty(activeProduct.id, usageDate, i, e.target.value)}
                        data-testid={`cell-split-input-${activeProduct.id}-${usageDate}-${i}`}
                        className="h-7 w-full text-sm border border-emerald-200 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-400 text-center font-mono px-1 rounded-sm"
                        placeholder="—"
                      />
                      <button type="button" onClick={() => onRemoveSplit(activeProduct.id, usageDate, i)} data-testid={`cell-split-remove-${activeProduct.id}-${usageDate}-${i}`} className="shrink-0 h-6 w-4 text-red-500 hover:bg-red-50 rounded text-xs leading-none">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {hasValue && (
                <button
                  type="button"
                  onClick={() => onAddSplit(activeProduct.id, usageDate)}
                  data-testid={`cell-split-add-${activeProduct.id}-${usageDate}`}
                  className="mt-1 w-full text-[10px] text-emerald-700 border border-dashed border-emerald-300 rounded px-1 py-0.5 hover:bg-emerald-50 leading-tight"
                >
                  ＋分ける
                </button>
              )}
            </td>
          </React.Fragment>
        );
      })}
    </tr>
  );
});

// スマホ判定（幅768px未満）
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
};

export default function StoreDashboard() {
  const COMMON_UNITS = useOrderUnits();
  const isMobile = useIsMobile();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [orderData, setOrderData] = useState({});
  const [removedLineIds, setRemovedLineIds] = useState([]); // 分割行削除で消したDB発注ID
  const [dateComments, setDateComments] = useState({}); // 日付ごとのコメント {usage_date: "コメント内容"}
  const [usageDates, setUsageDates] = useState({}); // 各使用日の配送希望日 { usage_date: delivery_date }
  const [historyProductIds, setHistoryProductIds] = useState([]);
  const [faxHistoryProductIds, setFaxHistoryProductIds] = useState([]);
  const [favoriteKeys, setFavoriteKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("weekly"); // 'weekly' | 'daily'
  const [selectedWeek, setSelectedWeek] = useState(0);
  // スマホでは週間グリッドが使えないため「日ごと入力」に固定
  useEffect(() => {
    if (isMobile) setMode("daily");
  }, [isMobile]);
  const [selectedDate, setSelectedDate] = useState("");
  const [dailyUsageDate, setDailyUsageDate] = useState(""); // 日ごと入力の使用日（配送日と独立して編集可）
  // フィルタ: 'all' | 'popular' | <category_id>
  const [filterKey, setFilterKey] = useState("history");
  // 本体サイドバーのフィルタ選択（?f=...）を反映
  const location = useLocation();
  useEffect(() => {
    const f = new URLSearchParams(location.search).get("f");
    if (f) setFilterKey(f);
  }, [location.search]);
  const [searchTerm, setSearchTerm] = useState("");
  const [multiColumnMode, setMultiColumnMode] = useState(false);
  const [columnNames, setColumnNames] = useState([]);
  const [storeData, setStoreData] = useState(null); // 店舗情報（顧客ランク含む）

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchData();
  }, []);

  // お気に入り（店舗単位）を読み込み
  useEffect(() => {
    if (!user?.store_id) return;
    axios
      .get(`${API}/store/favorites`, getAuthHeaders())
      .then((r) => setFavoriteKeys(r.data?.group_keys || []))
      .catch(() => {});
  }, []);

  const toggleFavorite = React.useCallback(async (groupKey) => {
    if (!groupKey) return;
    setFavoriteKeys((prev) => (prev.includes(groupKey) ? prev.filter((k) => k !== groupKey) : [...prev, groupKey]));
    try {
      const r = await axios.post(`${API}/store/favorites/toggle`, { group_key: groupKey }, getAuthHeaders());
      setFavoriteKeys(r.data?.group_keys || []);
    } catch (e) {
      toast.error("お気に入りの更新に失敗しました");
    }
  }, []);

  const moveFavorite = React.useCallback((groupKey, dir) => {
    setFavoriteKeys((prev) => {
      const i = prev.indexOf(groupKey);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      axios.put(`${API}/store/favorites/order`, { group_keys: next }, getAuthHeaders()).catch(() => {});
      return next;
    });
  }, []);

  const fetchData = async () => {
    try {
      // 表示ラベルのメタ（色・並び順）を先に取得してバッジ配色に反映
      try {
        const labRes = await axios.get(`${API}/product-labels`, getAuthHeaders());
        setLabelMeta(Array.isArray(labRes.data) ? labRes.data : []);
      } catch (e) {
        setLabelMeta([]);
      }
      const requests = [
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()),
        axios.get(`${API}/orders`, getAuthHeaders()),
        axios.get(`${API}/order-history/me`, getAuthHeaders()).catch(() => ({ data: [] })),
      ];

      // 店舗ユーザーの場合、店舗情報も取得
      if (user && user.store_id) {
        requests.push(axios.get(`${API}/stores/${user.store_id}`, getAuthHeaders()));
      }

      const responses = await Promise.all(requests);
      const [productsRes, categoriesRes, schedulesRes, ordersRes, historyRes] = responses;

      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSchedules(schedulesRes.data);
      const hist = historyRes.data;
      if (Array.isArray(hist)) {
        // 後方互換（旧API: 配列）
        setHistoryProductIds(hist);
        setFaxHistoryProductIds([]);
      } else {
        setHistoryProductIds(Array.isArray(hist?.history) ? hist.history : []);
        setFaxHistoryProductIds(Array.isArray(hist?.fax) ? hist.fax : []);
      }

      // 店舗情報がある場合
      if (responses[5]) {
        const store = responses[5].data;
        setStoreData(store);
        setMultiColumnMode(store.multi_column_mode || false);
        setColumnNames(store.column_names || []);
      }

      const productsMap = {};
      productsRes.data.forEach((p) => { productsMap[p.id] = p; });

      const initialData = {};
      const initialUsageDates = {};
      const initialComments = {};
      const byKey = {};
      const lastMap = {};
      ordersRes.data.forEach((o) => {
        if (o.cancelled || !(o.quantity > 0)) return;
        const k = `${o.delivery_date || ""}|${o.created_at || ""}`;
        if (!lastMap[o.product_id] || k > lastMap[o.product_id]) lastMap[o.product_id] = k;
      });
      setLastOrderedMap(lastMap);
      ordersRes.data.forEach((order) => {
        const key = `${order.product_id}_${order.usage_date}`;
        (byKey[key] = byKey[key] || []).push(order);
      });
      Object.entries(byKey).forEach(([key, arr]) => {
        arr.sort((a, b) => (a.line_no || 0) - (b.line_no || 0));
        const primary = arr[0];
        const product = productsMap[primary.product_id];
        const fallbackUnit = product?.default_order_unit || "g";
        initialData[key] = {
          id: primary.id,
          line_no: primary.line_no || 0,
          quantity: primary.quantity.toFixed(2),
          unit: primary.unit || fallbackUnit,
          breakdown: primary.breakdown || null,
          delivery_date: primary.delivery_date || null,
          // 事前取込(魚など)でデスク送信済み → 顧客は変更不可（電話対応）
          ep_locked: !!primary.confirmed && !primary.cancelled && !!product?.early_pull,
          orig_quantity: primary.quantity.toFixed(2),
          orig: { quantity: primary.quantity, unit: primary.unit || fallbackUnit, delivery_date: primary.delivery_date || null, comments: primary.comments || null },
          lines: arr.slice(1).map((o) => ({
            id: o.id,
            line_no: o.line_no,
            quantity: o.quantity.toFixed(2),
            unit: o.unit || fallbackUnit,
            delivery_date: o.delivery_date || null,
            orig: { quantity: o.quantity, unit: o.unit || fallbackUnit, delivery_date: o.delivery_date || null, comments: o.comments || null },
          })),
        };
        // usageDatesマッピング: {usage_date: delivery_date}
        if (primary.delivery_date && !initialUsageDates[primary.usage_date]) {
          initialUsageDates[primary.usage_date] = primary.delivery_date;
        }
        // 連絡事項（使用日ごと）を復元 — 上書き保存でnull送信して消えるのを防ぐ
        if (primary.comments && !initialComments[primary.usage_date]) {
          initialComments[primary.usage_date] = primary.comments;
        }
      });
      
      // 未入力の使用日はデフォルト空（顧客が配送希望日を手動選択）
      setOrderData(initialData);
      setUsageDates(initialUsageDates);
      setDateComments(initialComments);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = React.useCallback((productId, usageDate, value) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => ({
      ...prev,
      [key]: { ...prev[key], quantity: value },
    }));
  }, []);

  const handleUnitChange = React.useCallback((productId, usageDate, value) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => ({
      ...prev,
      [key]: { ...prev[key], unit: value },
    }));
  }, []);

  const handleDeliveryDateChange = React.useCallback((usageDate, deliveryDate) => {
    setUsageDates((prev) => ({
      ...prev,
      [usageDate]: deliveryDate,
    }));
  }, []);

  // 商品セルごとの配送希望日（列の既定を上書き）
  const handleCellDeliveryChange = React.useCallback((productId, usageDate, deliveryDate) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => ({
      ...prev,
      [key]: { ...prev[key], delivery_date: deliveryDate },
    }));
  }, []);

  // ── 用途分け（同一商品×同一使用日を複数明細に分割・合算しない）──
  const addSplitLine = React.useCallback((productId, usageDate) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => {
      const cur = prev[key] || { quantity: "", unit: "g" };
      const lines = cur.lines ? [...cur.lines] : [];
      lines.push({ quantity: "", unit: cur.unit || null, delivery_date: cur.delivery_date || null });
      return { ...prev, [key]: { ...cur, lines } };
    });
  }, []);

  const updateSplitLineQty = React.useCallback((productId, usageDate, idx, value) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => {
      const cur = prev[key];
      if (!cur || !cur.lines) return prev;
      const lines = cur.lines.map((ln, i) => (i === idx ? { ...ln, quantity: value } : ln));
      return { ...prev, [key]: { ...cur, lines } };
    });
  }, []);

  const updateSplitLineDelivery = React.useCallback((productId, usageDate, idx, value) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => {
      const cur = prev[key];
      if (!cur || !cur.lines) return prev;
      const lines = cur.lines.map((ln, i) => (i === idx ? { ...ln, delivery_date: value } : ln));
      return { ...prev, [key]: { ...cur, lines } };
    });
  }, []);

  const removeSplitLine = React.useCallback((productId, usageDate, idx) => {
    const key = `${productId}_${usageDate}`;
    setOrderData((prev) => {
      const cur = prev[key];
      if (!cur || !cur.lines) return prev;
      const removed = cur.lines[idx];
      if (removed && removed.id) setRemovedLineIds((r) => [...r, removed.id]);
      const lines = cur.lines.filter((_, i) => i !== idx);
      return { ...prev, [key]: { ...cur, lines } };
    });
  }, []);

  // グループ行のセルごとの単位選択（選択に応じて実商品=商品コードを切替）
  const [lastOrderedMap, setLastOrderedMap] = useState({}); // { productId: 直近の発注(配送日|作成日時) }
  const orderDataRef = React.useRef({});
  orderDataRef.current = orderData;
  const [groupUnitSel, setGroupUnitSel] = useState({}); // { `${groupKey}_${usageDate}`: productId }
  const handleCellUnitChange = React.useCallback((groupKey, usageDate, oldPid, newPid) => {
    if (oldPid === newPid) return;
    if (orderDataRef.current[`${oldPid}_${usageDate}`]?.ep_locked) {
      toast.error("事前取込済みの商品のため、単位の変更はお電話でお問い合わせください。");
      return;
    }
    setGroupUnitSel((prev) => ({ ...prev, [`${groupKey}_${usageDate}`]: newPid }));
    // 旧単位に入力済みの数量・配送日を新単位の商品へ移動（重複発注を防ぐ）
    // 旧単位の既存注文は空欄にして残す → 保存時に「取消」として削除される
    setOrderData((prev) => {
      const oldKey = `${oldPid}_${usageDate}`;
      const newKey = `${newPid}_${usageDate}`;
      const old = prev[oldKey];
      if (!old || !old.quantity) return prev;
      const next = { ...prev };
      const dst = prev[newKey] || {};
      next[newKey] = {
        ...dst,
        quantity: old.quantity,
        delivery_date: old.delivery_date,
        unit: undefined,
        lines: (old.lines || []).map(({ id, orig, ...rest }) => ({ ...rest, unit: undefined })),
      };
      if (old.id) {
        next[oldKey] = { ...old, quantity: "", lines: (old.lines || []).map((l) => ({ ...l, quantity: "" })) };
      } else {
        delete next[oldKey];
      }
      return next;
    });
  }, []);

  // 変更締切判定（店舗は変更締切後は直接編集不可）
  const isChangeLocked = (schedule) => {
    if (!schedule || schedule.is_dummy) return false;
    const dl = schedule?.change_deadline || schedule?.deadline;
    if (!dl) return false;
    const parsed = new Date(dl.replace(" ", "T"));
    return new Date() > parsed;
  };

  const isOrderDeadlinePassed = (schedule) => {
    if (!schedule || schedule.is_dummy) return false;
    const dl = schedule?.deadline;
    if (!dl) return false;
    return new Date() > new Date(dl.replace(" ", "T"));
  };

  // 週全体のロック判定：店舗の発注締切設定に基づいて計算
  const isWeekLocked = (weekSchedules) => {
    if (!weekSchedules || weekSchedules.length === 0) return false;
    if (!storeData) return false;
    
    // 週の最初の日（月曜日）を取得
    const firstSchedule = weekSchedules[0];
    if (!firstSchedule) return false;
    
    const firstDate = new Date(firstSchedule.delivery_date);
    const dow = firstDate.getDay();
    const daysToMonday = dow === 0 ? 6 : dow - 1;
    const monday = new Date(firstDate);
    monday.setDate(firstDate.getDate() - daysToMonday);
    
    // 店舗の発注締切設定を取得
    // order_deadline_weekday: 0=月, 1=火, 2=水, 3=木, 4=金, 5=土, 6=日
    const deadlineWeekday = storeData.order_deadline_weekday ?? 1; // デフォルト：火曜日
    const deadlineTime = storeData.order_deadline_time || "18:00"; // デフォルト：18:00
    
    // 前週の締切曜日を計算
    // 週の定義：月〜土の6日間（日曜は配送休み）
    // ルール：配送週の「前週の指定曜日・時刻」が締切
    // 例：8/3週(8/3月〜8/8土)の締切 = 前週(7/27月〜)の火曜 = 7/28
    // 前週の月曜 = 対象週の月曜 - 7、そこに deadlineWeekday を加算
    const prevWeekDeadline = new Date(monday);
    prevWeekDeadline.setDate(monday.getDate() - 7 + deadlineWeekday);
    
    // 締切時刻を設定
    const [hours, minutes] = deadlineTime.split(':').map(n => parseInt(n));
    prevWeekDeadline.setHours(hours, minutes, 0, 0);
    
    // 現在時刻が締切を過ぎているかチェック
    return new Date() > prevWeekDeadline;
  };

  // 指定した納品日が属する週の発注締切（前週の指定曜日・時刻）を返す
  const getDeadlineForDeliveryDate = (deliveryDateStr) => {
    if (!deliveryDateStr || !storeData) return null;
    const d = new Date(deliveryDateStr);
    if (isNaN(d.getTime())) return null;
    const dow = d.getDay();
    const daysToMonday = dow === 0 ? 6 : dow - 1;
    const monday = new Date(d);
    monday.setDate(d.getDate() - daysToMonday);
    const deadlineWeekday = storeData.order_deadline_weekday ?? 1;
    const deadlineTime = storeData.order_deadline_time || "18:00";
    const dl = new Date(monday);
    dl.setDate(monday.getDate() - 7 + deadlineWeekday);
    const [h, m] = deadlineTime.split(":").map((n) => parseInt(n));
    dl.setHours(h, m, 0, 0);
    return { deadline: dl, monday };
  };

  const [orderPreview, setOrderPreview] = useState(null);

  // 「発注を保存」: 内容を組み立ててプレビューを表示（送信は確認後）
  const handleSaveOrders = () => {
      const epChanged = Object.entries(orderData)
        .filter(([, info]) => info?.ep_locked && parseFloat(info.quantity || 0) !== parseFloat(info.orig_quantity || 0))
        .map(([key]) => products.find((p) => p.id === key.substring(0, key.lastIndexOf("_")))?.name)
        .filter(Boolean);
      if (epChanged.length > 0) {
        toast.error(`事前取込済みの商品（${[...new Set(epChanged)].join("、")}）は変更・キャンセルできません。お電話でお問い合わせください。`);
      }
      const ordersToSave = [];
      const previewLines = [];
      const clearedIds = [];
      const todayJst = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
      const missingDelivery = new Set();
      // 商品IDから商品を引くマップ（保存単位を画面表示と一致させるため）
      const productById = {};
      products.forEach((p) => { productById[p.id] = p; });
      Object.entries(orderData).forEach(([key, orderInfo]) => {
        const sep = key.lastIndexOf("_");
        const productId = key.substring(0, sep);
        const usageDate = key.substring(sep + 1);  // keyは usage_date ベース
        const prod = productById[productId];
        const pushLine = (info, lineNo) => {
          const qty = parseFloat(info?.quantity || 0);
          if (isNaN(qty) || qty <= 0) {
            // 既存の注文を空欄/0にした → 取消（従来は送信されず旧数量が残っていた）
            if (info?.id && info.orig && !info.ep_locked && !removedLineIds.includes(info.id) && (info.orig.delivery_date || "") >= todayJst) {
              clearedIds.push(info.id);
              previewLines.push({
                name: prod?.name || "",
                usage_date: usageDate,
                delivery_date: info.orig.delivery_date,
                quantity: info.orig.quantity,
                unit: info.orig.unit,
                changed: true,
                removed: true,
              });
            }
            return;
          }
          // 配送希望日: セル個別の指定 → 無ければ列(使用日)の既定を採用
          const deliveryDate = info?.delivery_date || usageDates[usageDate] || (mode === "daily" && usageDate === dailyUsageDate ? selectedDate : null);
          if (!deliveryDate) {
            missingDelivery.add(usageDate);
            return;
          }
          // 過去の配送日の明細は確認・送信の対象外（変更不可のため）
          if (deliveryDate < todayJst) return;
          // 単位: 明示選択 → 商品の実単位(画面表示と同じ) → デフォルト発注単位
          const resolvedUnit =
            info.unit || effectiveUnit(prod) || prod?.default_order_unit || null;
          const o = info.orig;
          const isNew = !o;
          const comments = dateComments[usageDate] || null;
          const changed = isNew || o.quantity !== qty || o.unit !== resolvedUnit || o.delivery_date !== deliveryDate || (o.comments || null) !== comments;
          previewLines.push({
            name: prod?.name || "",
            usage_date: usageDate,
            delivery_date: deliveryDate,
            quantity: qty,
            unit: resolvedUnit,
            isNew,
            changed,
          });
          ordersToSave.push({
            store_id: user.store_id,
            product_id: productId,
            usage_date: usageDate,  // 使用日（列ヘッダー・常にオープン）
            delivery_date: deliveryDate,
            quantity: qty,
            unit: resolvedUnit,
            comments: dateComments[usageDate] || null,  // 日付ごとのコメント
            line_no: lineNo,
          });
        };
        // 既存 line_no を尊重し、新規行のみ最大+1 で採番（連番衝突を防ぐ）
        const usedNos = [0];
        (orderInfo.lines || []).forEach((ln) => { if (ln.line_no != null) usedNos.push(ln.line_no); });
        let nextNo = Math.max(...usedNos) + 1;
        pushLine(orderInfo, 0);
        (orderInfo.lines || []).forEach((ln) => {
          const no = (ln.line_no != null) ? ln.line_no : nextNo++;
          pushLine(ln, no);
        });
      });

      if (missingDelivery.size > 0) {
        toast.error(
          `配送希望日を選択してください（使用日: ${[...missingDelivery]
            .sort()
            .map((d) => d.substring(5))
            .join(", ")}）`
        );
        return;
      }

      if (ordersToSave.length === 0 && removedLineIds.length === 0 && clearedIds.length === 0) {
        toast.error("保存する発注がありません");
        return;
      }
      previewLines.sort((a, b) => a.name.localeCompare(b.name, "ja") || a.usage_date.localeCompare(b.usage_date));
      setOrderPreview({ orders: ordersToSave, lines: previewLines, clearedIds, removedCount: new Set(removedLineIds).size });
  };

  // プレビューで「この内容で発注する」→ 送信
  const submitOrders = async () => {
    const ordersToSave = orderPreview?.orders || [];
    setSaving(true);
    try {
      // 空欄にした既存注文の取消（失敗＝締切後など はお知らせ）
      const failed = [];
      for (const id of orderPreview?.clearedIds || []) {
        try { await axios.delete(`${API}/orders/${id}`, getAuthHeaders()); } catch (e) { failed.push(e.response?.data?.detail || "取消できませんでした"); }
      }
      if (failed.length) toast.error(`取消できなかった明細があります：${[...new Set(failed)].join(" / ")}`);
      // 分割行の削除を先にDBへ反映
      if (removedLineIds.length > 0) {
        for (const id of [...new Set(removedLineIds)]) {
          try { await axios.delete(`${API}/orders/${id}`, getAuthHeaders()); } catch (e) { /* 締切後等は無視 */ }
        }
      }
      if (ordersToSave.length > 0) {
        await axios.post(`${API}/orders/bulk`, { orders: ordersToSave }, getAuthHeaders());
      }
      setRemovedLineIds([]);
      setOrderPreview(null);
      toast.success("発注を保存しました");
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "発注の保存に失敗しました。時間をおいて再度お試しください。";
      console.error("発注保存エラー:", error?.response?.status, detail || error?.message);
      toast.error(msg, { duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  // カテゴリ
  const categoriesMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c));
    return m;
  }, [categories]);

  // カテゴリ別商品数（入荷なし商品は除外）
  const productCountByCategory = useMemo(() => {
    const visible = products.filter((p) => !isHiddenFromStore(p));
    const histSet = new Set(historyProductIds);
    const faxSet = new Set(faxHistoryProductIds);
    const m = {
      all: visible.length,
      history: visible.filter((p) => histSet.has(p.id)).length,
      fax: visible.filter((p) => faxSet.has(p.id)).length,
      seasonal: visible.filter((p) => p.is_seasonal).length,
    };
    visible.forEach((p) => {
      const cid = categoriesMap[p.category_id] ? p.category_id : "__other__";
      m[cid] = (m[cid] || 0) + 1;
      (p.display_labels || []).forEach((lb) => {
        const key = LABEL_PREFIX + lb;
        m[key] = (m[key] || 0) + 1;
      });
    });
    return m;
  }, [products, categoriesMap, historyProductIds, faxHistoryProductIds]);

  // フィルタ＋ソート（旬 → カテゴリ指定順 → コード）
  // 入荷なし商品は店舗発注画面では非表示、検索キーワード適用
  const displayedProducts = useMemo(() => {
    let list = products.filter((p) => !isHiddenFromStore(p));
    if (filterKey === "seasonal") {
      list = list.filter((p) => p.is_seasonal);
    } else if (filterKey === "history") {
      const histSet = new Set(historyProductIds);
      list = list.filter((p) => histSet.has(p.id));
    } else if (filterKey === "fax") {
      const faxSet = new Set(faxHistoryProductIds);
      list = list.filter((p) => faxSet.has(p.id));
    } else if (filterKey === "favorites") {
      const favSet = new Set(favoriteKeys);
      list = list.filter((p) => favSet.has(groupKeyOf(p)));
    } else if (filterKey.startsWith(LABEL_PREFIX)) {
      const lb = filterKey.slice(LABEL_PREFIX.length);
      list = list.filter((p) => (p.display_labels || []).includes(lb));
    } else if (filterKey !== "all") {
      // category id, or "__other__"
      list = list.filter((p) => {
        const cid = categoriesMap[p.category_id] ? p.category_id : "__other__";
        return cid === filterKey;
      });
    }
    const term = searchTerm.trim();
    if (term) {
      list = list.filter((p) => fuzzyMatchJa(term, p.name, p.code, (p.search_keywords || []).join(" ")));
    }
    // お気に入りは登録順（並び替え順）で表示
    if (filterKey === "favorites") {
      const idx = new Map(favoriteKeys.map((k, i) => [k, i]));
      list.sort((a, b) => (idx.get(groupKeyOf(a)) ?? 9999) - (idx.get(groupKeyOf(b)) ?? 9999));
      return list;
    }
    const categoryOrder = (cname) => {
      if (!cname) return 99;
      if (cname.includes("カット野菜")) return 2;
      if (cname.includes("野菜")) return 1;
      if (cname.includes("果物")) return 3;
      if (cname.includes("加工")) return 4;
      return 50;
    };
    list.sort((a, b) => {
      // 1. 旬（季節商材）優先
      if (!!b.is_seasonal - !!a.is_seasonal !== 0) return !!b.is_seasonal - !!a.is_seasonal;
      // 2. カテゴリ順
      const oa = categoryOrder(categoriesMap[a.category_id]?.name);
      const ob = categoryOrder(categoriesMap[b.category_id]?.name);
      if (oa !== ob) return oa - ob;
      // 同順位カテゴリは名前順
      const ca = categoriesMap[a.category_id]?.name || "zzz";
      const cb = categoriesMap[b.category_id]?.name || "zzz";
      if (ca !== cb) return ca.localeCompare(cb);
      // 3. 商品名でソート(同じ商品名の商品を隣接表示)
      const nameCompare = (a.name || "").localeCompare(b.name || "", 'ja');
      if (nameCompare !== 0) return nameCompare;
      // 4. コード順
      return (a.code || "").localeCompare(b.code || "");
    });
    return list;
  }, [products, categoriesMap, filterKey, historyProductIds, faxHistoryProductIds, favoriteKeys, searchTerm]);

  // 表示用グループ（同名・異単位を1グループにまとめる。週間・日ごと共通）
  const displayedGroups = useMemo(() => buildProductGroups(displayedProducts), [displayedProducts]);

  // 同じ商品名が複数の単位で存在する場合を検出（単位バッジ強調＆kg推奨用）
  const { duplicateNames, kgRecommendNames } = useMemo(() => {
    const byName = {};
    displayedProducts.forEach((p) => {
      const n = p.name || "";
      const u = p.unit || "";
      if (!byName[n]) byName[n] = new Set();
      byName[n].add(u);
    });
    const dup = new Set();
    const kgRec = new Set();
    Object.entries(byName).forEach(([n, units]) => {
      if (units.size > 1) {
        dup.add(n);
        // 同名グループに kg 単位があれば kg 発注を推奨
        const hasKg = [...units].some(
          (u) => (u || "").toLowerCase().includes("kg") || (u || "").includes("㎏")
        );
        if (hasKg) kgRec.add(n);
      }
    });
    return { duplicateNames: dup, kgRecommendNames: kgRec };
  }, [displayedProducts]);

  // 週グループ化（月〜土）
  const sortedSchedules = [...schedules].sort(
    (a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)
  );
  const weekMap = new Map();
  sortedSchedules.forEach((schedule) => {
    const d = new Date(schedule.delivery_date);
    const dow = d.getDay();
    if (dow === 0) return;
    const days = dow === 0 ? 6 : dow - 1;
    const monday = new Date(d);
    monday.setDate(d.getDate() - days);
    const key = monday.toISOString().split("T")[0];
    if (!weekMap.has(key)) weekMap.set(key, []);
    weekMap.get(key).push(schedule);
  });
  // 終わった週（最終配送日が今日より過去）は除外
  const today = new Date().toISOString().split("T")[0];
  const sortedWeeks = Array.from(weekMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .filter(([, list]) => list.some((s) => s.delivery_date >= today))
    .map(([, list]) => list);

  // 流用ボタン用: 全週（過去含む）
  const allWeeks = Array.from(weekMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, list]) => list);

  // "X月第Y週" ラベル生成（その週の月曜の月の何週目か）
  const weekLabel = (weekSchedules) => {
    if (!weekSchedules || weekSchedules.length === 0) return "";
    const first = new Date(weekSchedules[0].delivery_date);
    const dow = first.getDay();
    const days = dow === 0 ? 6 : dow - 1;
    const monday = new Date(first);
    monday.setDate(first.getDate() - days);
    const month = monday.getMonth() + 1;
    const nth = Math.ceil(monday.getDate() / 7);
    return `${month}月第${nth}週`;
  };

  const weekMonthKey = (weekSchedules) => {
    if (!weekSchedules || weekSchedules.length === 0) return "";
    const first = new Date(weekSchedules[0].delivery_date);
    const dow = first.getDay();
    const days = dow === 0 ? 6 : dow - 1;
    const monday = new Date(first);
    monday.setDate(first.getDate() - days);
    return `${monday.getFullYear()}-${monday.getMonth() + 1}`;
  };

  // 選択された週の月〜土の6日分を生成（店舗の配送例外を考慮）
  const generateWeekDays = (weekSchedules) => {
    if (!weekSchedules || weekSchedules.length === 0) return [];
    
    // 週の最初のスケジュールから月曜日を計算
    const first = new Date(weekSchedules[0].delivery_date);
    const dow = first.getDay();
    const daysToMonday = dow === 0 ? 6 : dow - 1;
    const monday = new Date(first);
    monday.setDate(first.getDate() - daysToMonday);
    
    // 店舗の配送例外設定を取得
    const additionalDays = storeData?.additional_delivery_days || [];
    const skipDays = storeData?.skip_delivery_days || [];
    
    // 月〜土の6日分の日付を生成
    const weekDays = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay(); // 0=日, 1=月, ..., 6=土
      
      // この日付に対応する配送スケジュールを探す
      const schedule = weekSchedules.find(s => s.delivery_date === dateStr);
      
      // 配送可否の判定
      let isDeliveryDay = false;
      let isExceptionDay = false;
      
      if (schedule) {
        // 配送スケジュールマスタに存在する日
        if (skipDays.includes(dayOfWeek)) {
          // この店舗は配送休止日
          isDeliveryDay = false;
          isExceptionDay = true; // 例外日マーク
        } else {
          // 通常配送日
          isDeliveryDay = true;
        }
      } else {
        // 配送スケジュールマスタに存在しない日（通常休み、または backend が skip 除外済み）
        if (skipDays.includes(dayOfWeek)) {
          // この店舗の配送休止曜日 → 例外日として明示
          isDeliveryDay = false;
          isExceptionDay = true;
        } else if (additionalDays.includes(dayOfWeek)) {
          // この店舗は追加配送日
          isDeliveryDay = true;
          isExceptionDay = true; // 例外日マーク
        } else {
          // 配送なし
          isDeliveryDay = false;
        }
      }
      
      // スケジュールがない場合はダミーを作成
      weekDays.push(schedule || {
        id: `dummy-${dateStr}`,
        delivery_date: dateStr,
        order_deadline_date: '9999-12-31',
        change_deadline_date: '9999-12-31',
        is_dummy: true,
        is_delivery_day: isDeliveryDay,
        is_exception_day: isExceptionDay,
      });
      
      // スケジュールがある場合も配送可否フラグを追加
      if (schedule) {
        schedule.is_delivery_day = isDeliveryDay;
        schedule.is_exception_day = isExceptionDay;
      }
    }
    
    return weekDays;
  };

  const displayedSchedules = sortedWeeks[selectedWeek] 
    ? generateWeekDays(sortedWeeks[selectedWeek]) 
    : [];
  const selectedSchedule = schedules.find((s) => s.delivery_date === selectedDate);

  // 初期日付セット（初回のみ）
  useEffect(() => {
    if (!selectedDate && sortedSchedules.length > 0) {
      const today = new Date().toISOString().split("T")[0];
      const upcoming = sortedSchedules.find((s) => s.delivery_date >= today);
      setSelectedDate(upcoming ? upcoming.delivery_date : sortedSchedules[0].delivery_date);
    }
  }, [schedules, selectedDate, sortedSchedules]);

  // 日ごと入力: 配送希望日が変わったら使用日を配送日に初期同期（その後は独立編集可）
  useEffect(() => {
    if (selectedDate) setDailyUsageDate(selectedDate);
  }, [selectedDate]);

  // 日ごと入力: 使用日→配送希望日のマッピングを保存（保存時に delivery_date として使用）
  useEffect(() => {
    if (mode === "daily" && dailyUsageDate && selectedDate) {
      setUsageDates((prev) => ({ ...prev, [dailyUsageDate]: selectedDate }));
    }
  }, [mode, dailyUsageDate, selectedDate]);

  // 初期表示週: 過去週は除外済みなので 0 番目（最直近の週）に固定
  const [weekInitialized, setWeekInitialized] = useState(false);
  useEffect(() => {
    if (!weekInitialized && sortedWeeks.length > 0) {
      setSelectedWeek(0);
      setWeekInitialized(true);
    }
  }, [sortedWeeks, weekInitialized]);

  // 流用: 指定された source 週の発注を、現在表示中の週 (displayedSchedules) に
  // 曜日対応でコピー。締切後の対象日はスキップ。既存値は上書き（確認ダイアログ）。
  const copyFromWeek = (sourceWeek) => {
    const target = displayedSchedules;
    if (!target.length || !sourceWeek || !sourceWeek.length) return;
    const proceed = window.confirm(
      `${weekLabel(sourceWeek)} の発注を、現在表示している ${weekLabel(target)} に流用します。\n対象週の既存の入力値は上書きされます。よろしいですか？\n（締切を過ぎた日付はスキップされます）`
    );
    if (!proceed) return;

    // 曜日 (0=日, 1=月, ...) → source schedule
    const sourceByDow = new Map();
    sourceWeek.forEach((s) => {
      sourceByDow.set(new Date(s.delivery_date).getDay(), s);
    });

    const updates = {};
    let copied = 0;
    let skipped = 0;
    target.forEach((tgt) => {
      const locked = isOrderDeadlinePassed(tgt) || isChangeLocked(tgt);
      const src = sourceByDow.get(new Date(tgt.delivery_date).getDay());
      if (!src) return;
      products.forEach((p) => {
        // キーはusage_dateベース（schedulesはusage dateとして扱う）
        const srcUsageDate = src.delivery_date;
        const tgtUsageDate = tgt.delivery_date;
        const srcKey = `${p.id}_${srcUsageDate}`;
        const tgtKey = `${p.id}_${tgtUsageDate}`;
        const srcVal = orderData[srcKey];
        if (!srcVal || !srcVal.quantity || parseFloat(srcVal.quantity) <= 0) return;
        if (locked) {
          skipped += 1;
          return;
        }
        updates[tgtKey] = {
          quantity: String(srcVal.quantity),
          unit: srcVal.unit || p.default_order_unit || "g",
        };
        copied += 1;
      });
    });
    if (copied === 0) {
      toast.error("流用できる項目がありませんでした");
      return;
    }
    setOrderData((prev) => ({ ...prev, ...updates }));
    toast.success(
      `${copied} 件を流用しました${skipped > 0 ? `（${skipped} 件は締切後のためスキップ）` : ""}`
    );
  };

  // カテゴリ選択カード
  const renderCategoryCards = () => {
    const presentLabels = orderLabels(
      Object.keys(productCountByCategory)
        .filter((k) => k.startsWith(LABEL_PREFIX))
        .map((k) => k.slice(LABEL_PREFIX.length))
    );
    const labelCards = presentLabels.map((lb) => ({
      key: LABEL_PREFIX + lb,
      label: lb,
      count: productCountByCategory[LABEL_PREFIX + lb] || 0,
      palette: null,
      special: "label",
      labelName: lb,
    }));
    const cards = [
      { key: "all", label: "すべて", count: productCountByCategory.all || 0, palette: null, special: "all" },
      { key: "history", label: "履歴から選ぶ", count: productCountByCategory.history || 0, palette: null, special: "history" },
      { key: "fax", label: "FAX履歴", count: productCountByCategory.fax || 0, palette: null, special: "fax" },
      { key: "seasonal", label: "季節商品", count: productCountByCategory.seasonal || 0, palette: null, special: "seasonal" },
      ...labelCards,
      // その顧客に商品が1件も無いカテゴリ（件数0）は表示しない
      ...sortCategories(categories)
        .map((c) => ({
          key: c.id,
          label: c.name,
          count: productCountByCategory[c.id] || 0,
          palette: getCategoryPalette(c.id),
        }))
        .filter((c) => c.count > 0),
    ];
    if (productCountByCategory["__other__"]) {
      cards.push({
        key: "__other__",
        label: "その他",
        count: productCountByCategory["__other__"],
        palette: null,
        special: "other",
      });
    }
    const cardClass = (card, active) => {
      if (card.special === "all") {
        return active
          ? "bg-emerald-600 border-emerald-600 text-white shadow-sm"
          : "bg-white border-slate-300 text-slate-800 hover:border-emerald-400 hover:bg-emerald-50";
      }
      if (card.special === "history") {
        return active
          ? "bg-sky-600 border-sky-600 text-white shadow-sm"
          : "bg-sky-50 border-sky-200 text-sky-800 hover:bg-sky-100";
      }
      if (card.special === "fax") {
        return active
          ? "bg-teal-600 border-teal-600 text-white shadow-sm"
          : "bg-teal-50 border-teal-200 text-teal-800 hover:bg-teal-100";
      }
      if (card.special === "seasonal") {
        return active
          ? "bg-rose-500 border-rose-500 text-white shadow-sm"
          : "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100";
      }
      if (card.special === "other") {
        return active
          ? "bg-slate-700 border-slate-700 text-white shadow-sm"
          : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100";
      }
      if (card.special === "label") {
        const s = labelStyle(card.labelName);
        return active ? s.cardActive : `${s.cardBase} border`;
      }
      if (!card.palette) {
        return active
          ? "bg-slate-700 border-slate-700 text-white"
          : "bg-white border-slate-300 text-slate-800 hover:bg-slate-50";
      }
      return active ? card.palette.cardActive : card.palette.cardBase;
    };
    const leadIcon = (c, active) => {
      if (c.special === "history") return <Clock className={`h-3.5 w-3.5 shrink-0 ${active ? "text-white" : "text-sky-600"}`} />;
      if (c.special === "fax") return <Inbox className={`h-3.5 w-3.5 shrink-0 ${active ? "text-white" : "text-teal-600"}`} />;
      if (c.special === "label") return <Tag className={`h-3.5 w-3.5 shrink-0 ${active ? "text-white" : labelStyle(c.labelName).icon}`} />;
      if (c.special === "seasonal")
        return (
          <span className={`text-[9px] px-1 py-0.5 rounded-sm font-semibold shrink-0 ${active ? "bg-white/30 text-white" : "bg-rose-500 text-white"}`}>
            季節
          </span>
        );
      return null;
    };
    const Section = ({ title, items }) =>
      items.length === 0 ? null : (
        <div className="mb-3">
          <div className="text-[11px] font-semibold text-slate-400 px-2 mb-1 uppercase tracking-wide">{title}</div>
          <div className="space-y-1">
            {items.map((c) => {
              const active = filterKey === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setFilterKey(c.key)}
                  data-testid={`category-card-${c.key}`}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md border transition-all text-sm font-medium text-left ${cardClass(c, active)}`}
                >
                  {leadIcon(c, active)}
                  <span className="flex-1 truncate">{c.label}</span>
                  <span
                    className={`text-[11px] min-w-[1.25rem] px-1.5 rounded-md text-center shrink-0 ${active ? "bg-white/25 text-white" : "bg-white/90 text-slate-600 border border-slate-200"}`}
                  >
                    {c.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    const favItems = cards.filter((c) => ["all", "history", "fax"].includes(c.key));
    const featureItems = cards.filter((c) => c.special === "seasonal" || c.special === "label");
    const catItems = cards.filter(
      (c) => !["all", "history", "fax"].includes(c.key) && c.special !== "seasonal" && c.special !== "label"
    );
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-2 md:sticky md:top-4" data-testid="category-cards-container">
        <Section title="よく使う" items={favItems} />
        <Section title="特集・ラベル" items={featureItems} />
        <Section title="カテゴリー" items={catItems} />
      </div>
    );
  };

  // 検索バー ＋ kg単位のおすすめ注記（グリッド上部に配置）
  const renderNoProducts = () => (
    <div className="text-center py-10 px-4 text-slate-500 bg-white border border-slate-200 rounded-sm" data-testid="no-products-hint">
      {searchTerm.trim() ? (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-700">「{searchTerm.trim()}」に一致する商品が見つかりませんでした</p>
          <ul className="text-xs text-slate-500 leading-relaxed inline-block text-left">
            <li>・別の呼び方（ひらがな／カタカナ、略称）でもお試しください</li>
            <li>・一部の漢字名は読みで検索できない場合があります</li>
            <li>・それでも見つからない場合は「発注確認＆変更」ページの<span className="font-semibold text-amber-700">リストなし発注</span>からご依頼、または事務所へご相談ください</li>
          </ul>
        </div>
      ) : (
        <span className="text-sm">該当する商品がありません</span>
      )}
    </div>
  );

  const renderSearchBar = () => (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid="search-bar-container">
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="商品名・コードで検索（例：にんじん、ninjin、1010）"
          data-testid="product-search"
          lang="ja"
          inputMode="text"
          autoCapitalize="off"
          style={{ imeMode: "active" }}
          className="h-9 w-full pl-8 pr-3 rounded-md border border-slate-200 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-400 text-sm bg-white"
        />
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1" data-testid="kg-recommend-note">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>できるだけ <span className="font-bold">kg単位</span> でのご注文をおすすめします（個・本などは産地・規格により納品重量が変動する場合があります）。</span>
      </div>
    </div>
  );

  // 事前計算: 締切ロック・列背景
  // 発注締切後または変更締切後はこのページから直接編集不可（変更は「発注確認＆変更」から申請）
  // 週全体のロック判定を優先（週内の最も早い締切が過ぎたら全日ロック）
  const weeklyScheduleMeta = useMemo(
    () => {
      const weekLocked = isWeekLocked(displayedSchedules);
      
      // 週内のいずれかの日の締切が過ぎていれば、週全体をロック
      const hasAnyDeadlinePassed = displayedSchedules.some(s => 
        !s.is_dummy && isOrderDeadlinePassed(s)
      );
      
      return displayedSchedules.map((schedule, idx) => {
        const orderPassed = isOrderDeadlinePassed(schedule);
        const changeLocked = isChangeLocked(schedule);
        // 店舗の配送例外により配送しない日（配送休止曜日 or 配送外の曜日）
        const isNotDeliveryDay = schedule.is_delivery_day === false;
        return {
          schedule,
          idx,
          // 使用日の軸は常にオープン（配送休止/配送外の日でも数量入力可）。
          // 週全体ロック（締切）または変更締切時のみ入力不可。
          locked: weekLocked || hasAnyDeadlinePassed || orderPassed || changeLocked,
          weekLocked: weekLocked || hasAnyDeadlinePassed,  // 週全体がロックされているか
          orderPassed,
          changeLocked,
          isNotDeliveryDay,
          isExceptionDay: schedule.is_exception_day === true,
          weekday: getWeekday(schedule.delivery_date),
          bg: dayBg(idx),
          headerBg: dayHeaderBg(idx),
        };
      });
    },
    [displayedSchedules, storeData]
  );

  const renderWeeklyTable = () => {
    // 週全体のロック状態をチェック
    const weekLocked = isWeekLocked(displayedSchedules);
    
    // 週内のいずれかの日の締切が過ぎていれば、週全体をロック
    const hasAnyDeadlinePassed = displayedSchedules.some(s => 
      !s.is_dummy && isOrderDeadlinePassed(s)
    );
    
    // 警告メッセージ表示条件: 週ロックまたはいずれかの日の締切が過ぎている
    const showWarning = weekLocked || hasAnyDeadlinePassed;

    // セル個別の配送希望日で選べる候補（配送可能日のみ）
    const weeklyDeliveryOptions = weeklyScheduleMeta
      .filter(({ isNotDeliveryDay }) => !isNotDeliveryDay)
      .map(({ schedule, weekday }) => ({
        date: schedule.delivery_date,
        label: `${fmtUsageDate(schedule.delivery_date)}(${weekday})`,
      }));

    // 前週の金・土も配送希望日として選べるようにする
    // （例: 月曜使用分を前週金曜に納品したいケース。締切は使用日の週基準で判定）
    const mondayStr = weeklyScheduleMeta[0]?.schedule?.delivery_date;
    if (mondayStr) {
      const mon = new Date(mondayStr);
      const skipDays = storeData?.skip_delivery_days || [];
      // 金曜 = 月曜-3日, 土曜 = 月曜-2日。unshiftで先頭挿入するため 土→金 の順で処理し、最終的に 金→土 の並びにする
      [2, 3].forEach((minus) => {
        const d = new Date(mon);
        d.setDate(mon.getDate() - minus);
        const ds = d.toISOString().split("T")[0];
        const sched = sortedSchedules.find((s) => s.delivery_date === ds);
        if (sched && !skipDays.includes(d.getDay())) {
          weeklyDeliveryOptions.unshift({
            date: ds,
            label: `${fmtUsageDate(ds)}(${getWeekday(ds)})・前週`,
          });
        }
      });
    }

    return (
      <div className="space-y-3">
        {/* 週全体がロックされている場合の警告 */}
        {showWarning && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <div>
              <span className="font-semibold">この週は締切済みです。</span>
              <span className="ml-1">新規入力・変更は「発注確認＆変更」ページから申請してください。</span>
            </div>
          </div>
        )}
        
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="max-h-[70vh] overflow-auto">
        <table className="w-full border-collapse">
          <thead className="bg-slate-50 sticky top-0 z-10 shadow-[inset_0_-1px_0_#e2e8f0]">
            {/* 1行目：使用日（青い背景・右上配置） */}
            <tr>
              <th className="h-12 px-2 text-right align-middle font-semibold text-slate-600 text-xs w-64 sticky left-0 bg-slate-50 z-20 border-r border-slate-200">
                コード / 商品名
              </th>
              <th className="h-12 px-1 text-center align-middle font-semibold text-slate-600 text-[11px] whitespace-nowrap w-14 sticky left-64 bg-slate-50 z-20 border-r border-slate-300">
                発注<br />単位
              </th>
              <th className="h-12 px-1 text-center align-middle font-semibold text-slate-600 text-[11px] whitespace-nowrap w-24 sticky left-[19rem] bg-slate-50 z-20 border-r border-slate-300">
                目安<br />重量
              </th>
              <th className="h-12 px-1 text-center align-middle font-semibold text-slate-600 text-[11px] whitespace-nowrap w-20 sticky left-[24rem] bg-slate-50 z-20 border-r-2 border-slate-300">
                参考単価
              </th>
              {weeklyScheduleMeta.map(({ schedule, changeLocked, orderPassed, weekLocked, weekday, isNotDeliveryDay, isExceptionDay }, colIdx) => (
                <th
                  key={schedule.id}
                  colSpan={2}
                  className="h-20 px-2 align-middle border-r-2 border-slate-200 bg-emerald-50"
                >
                  <div className="flex flex-col items-center justify-center h-full gap-1">
                    {/* 使用日（常にオープン・最重要情報として大きく強調） */}
                    <div
                      className="w-full rounded-md bg-emerald-600 text-white px-2 py-1 text-center shadow-sm"
                      data-testid={`usage-date-header-${schedule.delivery_date}`}
                      title={`${schedule.delivery_date}（${weekday}）に使用する分の発注です`}
                    >
                      <div className="text-[9px] font-medium leading-none opacity-90">
                        この日に使う分
                      </div>
                      <div className="text-base font-bold leading-tight mt-0.5 flex items-center justify-center gap-1">
                        <span aria-hidden="true">🗓️</span>
                        {fmtUsageDate(schedule.delivery_date)}
                        <span className="text-xs font-semibold">（{weekday}）</span>
                      </div>
                    </div>
                    {/* 配送希望日ドロップダウン（全列表示・デフォルト空・店舗の配送可能日から選択） */}
                    <div className="flex flex-col items-center gap-0.5 w-full">
                      <label className="text-[10px] text-slate-600 font-medium">📦 配送希望日</label>
                      <select
                        value={usageDates[schedule.delivery_date] || ""}
                        onChange={(e) => handleDeliveryDateChange(schedule.delivery_date, e.target.value)}
                        disabled={weekLocked}
                        data-testid={`delivery-date-select-${schedule.delivery_date}`}
                        className="w-full px-2 py-1 text-xs border border-emerald-300 rounded bg-white text-center focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
                      >
                        <option value="">選択してください</option>
                        {weeklyDeliveryOptions.map((opt) => (
                          <option key={opt.date} value={opt.date}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* 締切表示（週全体ロックを優先表示） */}
                    {weekLocked ? (
                      <div className="text-red-600 flex items-center gap-1 text-[10px]">
                        <AlertCircle className="h-3 w-3" />
                        <span className="font-semibold">締切済み</span>
                      </div>
                    ) : changeLocked ? (
                      <div className="text-red-600 flex items-center gap-1 text-[10px]">
                        <AlertCircle className="h-3 w-3" />
                        <span className="font-semibold">変更締切</span>
                      </div>
                    ) : orderPassed ? (
                      <div className="text-amber-600 flex items-center gap-1 text-[10px]">
                        <AlertCircle className="h-3 w-3" />
                        <span className="font-semibold">通常発注締切済み</span>
                      </div>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
            {/* 2行目：単位と数量 */}
            <tr>
              <th className="h-7 px-4 text-left align-middle font-medium text-slate-500 text-[11px] bg-slate-50 sticky left-0 z-20 border-r border-slate-200"></th>
              <th className="h-7 px-2 text-left align-middle font-medium text-slate-500 text-[11px] bg-slate-50 sticky left-64 z-20 border-r border-slate-300"></th>
              <th className="h-7 px-2 text-left align-middle font-medium text-slate-500 text-[11px] bg-slate-50 sticky left-[19rem] z-20 border-r border-slate-300"></th>
              <th className="h-7 px-2 text-left align-middle font-medium text-slate-500 text-[11px] bg-slate-50 sticky left-[24rem] z-20 border-r-2 border-slate-300"></th>
              {weeklyScheduleMeta.map(({ schedule }) => (
                <React.Fragment key={`sub-${schedule.id}`}>
                  <th className="h-7 px-1 text-center align-middle font-medium text-slate-500 text-[11px] border-r border-slate-100 w-16 bg-emerald-50">
                    単位
                  </th>
                  <th className="h-7 px-1 text-center align-middle font-medium text-slate-500 text-[11px] border-r-2 border-slate-200 w-28 bg-emerald-50">
                    数量
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedGroups.map((group, idx) => {
              const isOdd = idx % 2 === 1;
              const rowBg = isOdd ? "bg-slate-50" : "";
              const stickyBg = isOdd ? "bg-slate-50" : "bg-white";
              // このグループ(全メンバー)分の orderData と単位選択だけを切り出して渡す
              const rowOrders = {};
              const unitSel = {};
              weeklyScheduleMeta.forEach(({ schedule }) => {
                const usageDate = schedule.delivery_date;
                group.members.forEach((m) => {
                  const k = `${m.id}_${usageDate}`;
                  if (orderData[k]) rowOrders[k] = orderData[k];
                });
                const selK = `${group.groupKey}_${usageDate}`;
                unitSel[usageDate] = groupUnitSel[selK] || defaultGroupMemberId(group, usageDate, orderData, lastOrderedMap);
              });
              return (
                <WeeklyRow
                  key={group.groupKey}
                  group={group}
                  scheduleMeta={weeklyScheduleMeta}
                  rowOrders={rowOrders}
                  unitSel={unitSel}
                  rowBg={rowBg}
                  stickyBg={stickyBg}
                  onQuantityChange={handleQuantityChange}
                  onCellDeliveryChange={handleCellDeliveryChange}
                  onCellUnitChange={(usageDate, oldPid, newPid) => handleCellUnitChange(group.groupKey, usageDate, oldPid, newPid)}
                  deliveryOptions={weeklyDeliveryOptions}
                  columnDefaults={usageDates}
                  customerRank={storeData?.customer_rank || "a1"}
                  highlightUnit={duplicateNames.has(group.members[0].name)}
                  kgRecommend={kgRecommendNames.has(group.members[0].name)}
                  onAddSplit={addSplitLine}
                  onUpdateSplitQty={updateSplitLineQty}
                  onUpdateSplitDelivery={updateSplitLineDelivery}
                  onRemoveSplit={removeSplitLine}
                  isFav={favoriteKeys.includes(group.groupKey)}
                  onToggleFav={() => toggleFavorite(group.groupKey)}
                  showReorder={filterKey === "favorites"}
                  canUp={favoriteKeys.indexOf(group.groupKey) > 0}
                  canDown={favoriteKeys.indexOf(group.groupKey) >= 0 && favoriteKeys.indexOf(group.groupKey) < favoriteKeys.length - 1}
                  onMoveUp={() => moveFavorite(group.groupKey, -1)}
                  onMoveDown={() => moveFavorite(group.groupKey, 1)}
                />
              );
            })}
          </tbody>
          <tfoot>
            {/* コメント行 */}
            <tr className="bg-amber-50 border-t-2 border-amber-200">
              <td colSpan="4" className="px-4 py-2 text-xs font-semibold text-amber-800 sticky left-0 z-20 bg-amber-50 border-r-2 border-slate-300">
                💬 顧客からの連絡事項
              </td>
              {weeklyScheduleMeta.map(({ schedule }) => (
                <td
                  key={`comment-${schedule.id}`}
                  colSpan={2}
                  className="px-2 py-1.5 border-r-2 border-slate-200 bg-white"
                >
                  <textarea
                    value={dateComments[schedule.delivery_date] || ""}
                    onChange={(e) => setDateComments(prev => ({...prev, [schedule.delivery_date]: e.target.value}))}
                    placeholder="この日のコメント..."
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
                    rows="2"
                  />
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
      </div>
    );
  };

  // ── 日ごと入力テーブル（全カテゴリ統合・1テーブル） ──
  const renderDailyTable = () => {
    const locked =
      isChangeLocked(selectedSchedule) || isOrderDeadlinePassed(selectedSchedule);

    // セル個別の配送希望日で選べる候補（配送スケジュールのある日）
    const dailyDeliveryOptions = sortedSchedules.map((s) => ({
      date: s.delivery_date,
      label: `${fmtUsageDate(s.delivery_date)}（${getWeekday(s.delivery_date)}）`,
    }));

    // 数量ステッパー（+/-）
    const stepQty = (pid, cur, delta) => {
      const n = Math.max(0, Math.round(((parseFloat(cur) || 0) + delta) * 100) / 100);
      handleQuantityChange(pid, dailyUsageDate, n === 0 ? "" : String(n));
    };

    // ===== スマホ用: カード型＋数量ステッパー UI =====
    if (isMobile) {
      const rank = storeData?.customer_rank || "a1";
      return (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm w-full">
          {/* 配送日 / 使用日 */}
          <div className="bg-emerald-50 border-b-2 border-emerald-200 p-3 space-y-3">
            <div>
              <label className="text-xs font-semibold text-emerald-700 mb-1 block">📦 配送希望日</label>
              <DeliveryDatePicker value={selectedDate} onChange={setSelectedDate} schedules={sortedSchedules} testId="daily-delivery-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-emerald-700 mb-1 block">🗓️ 使用日（編集可）</label>
              <input
                type="date"
                value={dailyUsageDate || selectedDate}
                onChange={(e) => setDailyUsageDate(e.target.value)}
                disabled={locked}
                data-testid="daily-usage-date"
                className="w-full h-10 px-3 text-sm border border-emerald-300 rounded bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-100"
              />
            </div>
          </div>

          <div className="px-3 py-2 text-xs text-slate-500 border-b border-slate-100">
            全 <span className="font-bold text-slate-700">{displayedGroups.length}</span> 件
          </div>

          {/* 商品カード一覧 */}
          <div className="divide-y divide-slate-100 pb-28">
            {displayedGroups.map((group) => {
              const gIsGroup = group.isGroup;
              const selId = gIsGroup
                ? (groupUnitSel[`${group.groupKey}_${dailyUsageDate}`] || defaultGroupMemberId(group, dailyUsageDate, orderData, lastOrderedMap))
                : group.members[0].id;
              const product = group.members.find((m) => m.id === selId) || group.members[0];
              const displayName = gIsGroup ? group.baseName : product.name;
              const key = `${product.id}_${dailyUsageDate}`;
              const orderInfo = orderData[key] || { quantity: "", unit: effectiveUnit(product) || "g" };
              const hasValue = orderInfo.quantity && parseFloat(orderInfo.quantity) > 0;
              const pm = gIsGroup ? priceBearingMember(group, rank) : product;
              const price = pm.prices?.[rank];
              const cellDelivery = orderInfo.delivery_date || selectedDate;
              const isOverride = orderInfo.delivery_date && orderInfo.delivery_date !== selectedDate;
              return (
                <div key={group.groupKey} className={`p-3 ${hasValue ? "bg-lime-50" : ""}`} data-testid={`daily-card-${product.id}`}>
                  <div className="flex items-start gap-3">
                    {/* 左: 商品名・価格・バッジ */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 text-[15px] leading-snug break-words flex items-start gap-1.5">
                        {filterKey === "favorites" && (
                          <span className="flex flex-col shrink-0 mt-0.5">
                            <button type="button" onClick={() => moveFavorite(group.groupKey, -1)} disabled={favoriteKeys.indexOf(group.groupKey) <= 0} data-testid={`fav-up-${group.groupKey}`} className="h-3.5 text-slate-400 hover:text-emerald-600 disabled:opacity-25"><ChevronUp className="h-4 w-4" /></button>
                            <button type="button" onClick={() => moveFavorite(group.groupKey, 1)} disabled={favoriteKeys.indexOf(group.groupKey) === favoriteKeys.length - 1} data-testid={`fav-down-${group.groupKey}`} className="h-3.5 text-slate-400 hover:text-emerald-600 disabled:opacity-25"><ChevronDown className="h-4 w-4" /></button>
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleFavorite(group.groupKey)}
                          data-testid={`fav-toggle-${group.groupKey}`}
                          title={favoriteKeys.includes(group.groupKey) ? "お気に入り解除" : "お気に入りに追加"}
                          className="shrink-0 mt-0.5 p-0.5 rounded hover:bg-amber-50"
                        >
                          <Star className={`h-4 w-4 ${favoriteKeys.includes(group.groupKey) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                        </button>
                        <span>{displayName}{(orderInfo.lines || []).length > 0 ? circledNum(1) : ""}</span>
                      </div>
                      <div className="flex items-center flex-wrap gap-1.5 mt-1">
                        {price ? <span className="text-sm text-slate-600 font-medium"><span className="text-[10px] text-slate-400 mr-0.5">参考</span>¥{price}</span> : <span className="text-sm text-slate-400">¥—</span>}
                        {(gIsGroup || (kgRecommendNames.has(product.name) && !((product.unit || "").toLowerCase().includes("kg") || (product.unit || "").includes("㎏")))) && <KgRecommendMark />}
                        <LabelBadges labels={gIsGroup ? productLabels(group) : product.display_labels} />
                        {(() => {
                          const opt = getSeasonalStatusOption(product.seasonal_status);
                          if (opt) return <span className={`px-1 py-0 rounded-sm font-semibold text-[10px] ${opt.pillClass}`}>{opt.label}</span>;
                          if (product.is_seasonal) return <span className="px-1 py-0 rounded-sm bg-emerald-500 text-white font-semibold text-[10px]">季節</span>;
                          return null;
                        })()}
                        {product.is_out_of_stock && product.seasonal_status !== "out_of_stock" && (
                          <span className="px-1 py-0 rounded-sm bg-red-500 text-white font-semibold text-[10px]">入荷なし</span>
                        )}
                      </div>
                      {product.estimated_weight && (
                        <div className="text-[11px] text-slate-400 mt-0.5">目安: {product.estimated_weight}</div>
                      )}
                      {orderInfo.ep_locked && (
                        <div data-testid={`daily-ep-locked-${product.id}`} className="text-[11px] text-sky-700 mt-0.5">事前取込済・変更はお電話で</div>
                      )}
                    </div>

                    {/* 右: 数量ステッパー + 単位 */}
                    <div className="shrink-0 flex flex-col items-center">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => stepQty(product.id, orderInfo.quantity, -1)}
                          disabled={locked || !hasValue || orderInfo.ep_locked}
                          data-testid={`daily-minus-${product.id}`}
                          className="h-9 w-9 rounded-full bg-slate-200 text-slate-600 text-xl font-bold flex items-center justify-center active:bg-slate-300 disabled:opacity-40"
                        >−</button>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          inputMode="decimal"
                          value={orderInfo.quantity}
                          onChange={(e) => handleQuantityChange(product.id, dailyUsageDate, e.target.value)}
                          disabled={locked || orderInfo.ep_locked}
                          data-daily-input="1"
                          data-testid={`daily-input-${product.id}`}
                          placeholder="0"
                          className={`w-16 h-11 px-1 text-center text-lg font-mono border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:bg-slate-100 disabled:text-slate-400 ${hasValue ? "border-emerald-400 text-emerald-700 font-bold bg-white" : "border-slate-300 text-slate-500"}`}
                        />
                        <button
                          type="button"
                          onClick={() => stepQty(product.id, orderInfo.quantity, 1)}
                          disabled={locked || orderInfo.ep_locked}
                          data-testid={`daily-plus-${product.id}`}
                          className="h-9 w-9 rounded-full bg-emerald-500 text-white text-xl font-bold flex items-center justify-center active:bg-emerald-600 disabled:opacity-40"
                        >＋</button>
                      </div>
                      {/* 単位 */}
                      <div className="mt-1">
                        {gIsGroup ? (
                          <select
                            value={product.id}
                            disabled={locked}
                            onChange={(e) => handleCellUnitChange(group.groupKey, dailyUsageDate, product.id, e.target.value)}
                            data-testid={`daily-cell-unit-${group.groupKey}`}
                            className="h-7 text-xs border border-slate-300 rounded bg-white px-1 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                          >
                            {group.members.map((m) => (
                              <option key={m.id} value={m.id}>{effectiveUnit(m) || "—"}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-slate-500 font-medium">{effectiveUnit(product) || "—"}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* 別日配送（数量入力時のみ） */}
                  {hasValue && !locked && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <span className="text-[11px] text-slate-500 shrink-0">📦 配送日:</span>
                      <select
                        value={cellDelivery}
                        onChange={(e) => handleCellDeliveryChange(product.id, dailyUsageDate, e.target.value)}
                        data-testid={`daily-cell-delivery-${product.id}`}
                        className={`flex-1 h-8 px-2 text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400 ${isOverride ? "border-amber-400 text-amber-700 font-semibold" : "border-slate-300 text-slate-600"}`}
                      >
                        {dailyDeliveryOptions.map((o) => (
                          <option key={o.date} value={o.date}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* 用途分け（分けて発注・合算しない別明細） */}
                  {!locked && (
                    <div className="mt-2 space-y-1.5">
                      {(orderInfo.lines || []).map((ln, i) => {
                        const lnHas = ln.quantity && parseFloat(ln.quantity) > 0;
                        return (
                          <div key={i} className="flex items-center gap-1.5 bg-white/70 border border-emerald-200 rounded-lg p-1.5" data-testid={`daily-split-${product.id}-${i}`}>
                            <span className="shrink-0 text-xs font-bold text-emerald-700 w-5 text-center">{circledNum(i + 2)}</span>
                            <input
                              type="number" step="0.01" min="0" inputMode="decimal"
                              value={ln.quantity}
                              onChange={(e) => updateSplitLineQty(product.id, dailyUsageDate, i, e.target.value)}
                              data-testid={`daily-split-input-${product.id}-${i}`}
                              placeholder="0"
                              className={`w-16 h-9 px-1 text-center text-base font-mono border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300 ${lnHas ? "border-emerald-400 text-emerald-700 font-bold bg-white" : "border-slate-300 text-slate-500"}`}
                            />
                            <span className="text-xs text-slate-500 shrink-0">{effectiveUnit(product) || "—"}</span>
                            <select
                              value={ln.delivery_date || ""}
                              onChange={(e) => updateSplitLineDelivery(product.id, dailyUsageDate, i, e.target.value)}
                              data-testid={`daily-split-delivery-${product.id}-${i}`}
                              className="flex-1 min-w-0 h-8 px-1 text-[11px] border rounded bg-white border-slate-300 text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                            >
                              <option value="">配送日:主と同じ</option>
                              {dailyDeliveryOptions.map((o) => (<option key={o.date} value={o.date}>{o.label}</option>))}
                            </select>
                            <button type="button" onClick={() => removeSplitLine(product.id, dailyUsageDate, i)} data-testid={`daily-split-remove-${product.id}-${i}`} className="shrink-0 h-8 w-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center text-sm">✕</button>
                          </div>
                        );
                      })}
                      {hasValue && (
                        <button
                          type="button"
                          onClick={() => addSplitLine(product.id, dailyUsageDate)}
                          data-testid={`daily-split-add-${product.id}`}
                          className="text-xs text-emerald-700 border border-dashed border-emerald-300 rounded-lg px-2 py-1 hover:bg-emerald-50"
                        >
                          ＋分けて発注（用途違いで別明細に）
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {displayedGroups.length === 0 && (
              <div className="p-4">{renderNoProducts()}</div>
            )}
          </div>

          {/* 連絡事項 */}
          <div className="p-3 bg-amber-50 border-t-2 border-amber-200">
            <label className="text-xs font-semibold text-amber-800 block mb-1">💬 連絡事項</label>
            <textarea
              value={dateComments[dailyUsageDate] || ""}
              onChange={(e) => setDateComments((prev) => ({ ...prev, [dailyUsageDate]: e.target.value }))}
              disabled={locked}
              rows={2}
              placeholder="配送に関する連絡事項があれば入力"
              className="w-full text-sm border border-amber-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:bg-slate-100"
            />
          </div>

          {/* 下部固定 保存バー */}
          <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 p-3 flex gap-3 no-print">
            <Button
              onClick={handleSaveOrders}
              disabled={saving || locked}
              data-testid="mobile-save-orders-button"
              className="flex-1 h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow"
            >
              {saving ? "保存中..." : "この内容で保存"}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm w-full max-w-4xl">
        {/* 配送日と使用日のヘッダー */}
        <div className="bg-emerald-50 border-b-2 border-emerald-200 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="w-full sm:flex-1">
              <label className="text-xs font-semibold text-emerald-700 mb-1 block">
                📦 配送希望日
              </label>
              <DeliveryDatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                schedules={sortedSchedules}
                testId="daily-delivery-date"
              />
            </div>
            <div className="w-full sm:flex-1">
              <label className="text-xs font-semibold text-emerald-700 mb-1 block">
                🗓️ 使用日（編集可）
              </label>
              <input
                type="date"
                value={dailyUsageDate || selectedDate}
                onChange={(e) => setDailyUsageDate(e.target.value)}
                disabled={locked}
                data-testid="daily-usage-date"
                className="w-full h-10 px-3 text-sm border border-emerald-300 rounded bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-100 disabled:text-slate-400"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                配送日と別の日に使用する場合は変更できます
              </p>
            </div>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0 z-10 shadow-[inset_0_-1px_0_#e2e8f0]">
              <tr>
                <th className="h-9 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase max-w-md">
                  コード / 商品名
                </th>
                <th className="h-9 px-2 text-left align-middle font-medium text-slate-500 text-xs uppercase w-24">
                  発注単位
                </th>
                <th className="h-9 px-2 text-left align-middle font-medium text-slate-500 text-xs uppercase w-24">
                  目安重量
                </th>
                <th className="h-9 px-2 text-right align-middle font-medium text-slate-500 text-xs uppercase w-24">
                  参考単価
                </th>
                <th className="h-9 px-2 text-center align-middle font-medium text-slate-500 text-xs uppercase w-48">
                  数量
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedGroups.map((group, idx) => {
                const gIsGroup = group.isGroup;
                const selId = gIsGroup
                  ? (groupUnitSel[`${group.groupKey}_${dailyUsageDate}`] || defaultGroupMemberId(group, dailyUsageDate, orderData, lastOrderedMap))
                  : group.members[0].id;
                const product = group.members.find((m) => m.id === selId) || group.members[0];
                const displayName = gIsGroup ? group.baseName : product.name;
                const key = `${product.id}_${dailyUsageDate}`;
                const orderInfo =
                  orderData[key] || { quantity: "", unit: effectiveUnit(product) || "g" };
                const hasValue = orderInfo.quantity && parseFloat(orderInfo.quantity) > 0;
                const rowBg = idx % 2 === 0 ? "" : "bg-slate-100/60";
                return (
                  <tr
                    key={group.groupKey}
                    className={`hover:bg-slate-100/70 ${rowBg}`}
                  >
                    <td className="py-2 px-4 align-middle text-sm">
                      <div className="flex items-center gap-1.5">
                        {filterKey === "favorites" && (
                          <span className="flex flex-col shrink-0">
                            <button type="button" onClick={() => moveFavorite(group.groupKey, -1)} disabled={favoriteKeys.indexOf(group.groupKey) <= 0} data-testid={`fav-up-${group.groupKey}`} className="h-3 text-slate-400 hover:text-emerald-600 disabled:opacity-25"><ChevronUp className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={() => moveFavorite(group.groupKey, 1)} disabled={favoriteKeys.indexOf(group.groupKey) === favoriteKeys.length - 1} data-testid={`fav-down-${group.groupKey}`} className="h-3 text-slate-400 hover:text-emerald-600 disabled:opacity-25"><ChevronDown className="h-3.5 w-3.5" /></button>
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleFavorite(group.groupKey)}
                          data-testid={`fav-toggle-${group.groupKey}`}
                          title={favoriteKeys.includes(group.groupKey) ? "お気に入り解除" : "お気に入りに追加"}
                          className="shrink-0 p-0.5 rounded hover:bg-amber-50"
                        >
                          <Star className={`h-4 w-4 ${favoriteKeys.includes(group.groupKey) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                        </button>
                        <span className="font-medium text-slate-800 truncate">{displayName}{(orderInfo.lines || []).length > 0 ? circledNum(1) : ""}</span>
                        {(gIsGroup || (kgRecommendNames.has(product.name) &&
                          !((product.unit || "").toLowerCase().includes("kg") || (product.unit || "").includes("㎏")))) && (
                            <KgRecommendMark />
                          )}
                        <LabelBadges labels={gIsGroup ? productLabels(group) : product.display_labels} />
                        {(() => {
                          const opt = getSeasonalStatusOption(product.seasonal_status);
                          if (opt) {
                            return (
                              <span className={`shrink-0 px-1 py-0 rounded-sm font-semibold text-[10px] ${opt.pillClass}`}>
                                {opt.label}
                              </span>
                            );
                          }
                          if (product.is_seasonal) {
                            return (
                              <span className="shrink-0 px-1 py-0 rounded-sm bg-emerald-500 text-white border border-emerald-600 font-semibold text-[10px]">
                                季節
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {product.is_out_of_stock && product.seasonal_status !== "out_of_stock" && (
                          <span className="shrink-0 px-1 py-0 rounded-sm bg-red-500 text-white border border-red-600 font-semibold text-[10px]">
                            入荷なし
                          </span>
                        )}
                      </div>
                      {orderInfo.ep_locked && (
                        <div data-testid={`daily-ep-locked-${product.id}`} className="mt-0.5 text-[11px] text-sky-700 font-medium">事前取込済・変更はお電話で</div>
                      )}
                    </td>
                    <td className="py-2 px-2 align-middle text-xs text-slate-700 font-medium">
                      {gIsGroup ? (
                        <select
                          value={product.id}
                          disabled={locked}
                          onChange={(e) => handleCellUnitChange(group.groupKey, dailyUsageDate, product.id, e.target.value)}
                          data-testid={`daily-cell-unit-${group.groupKey}`}
                          title="単位を選択（対応する商品コードで発注されます）"
                          className="h-8 w-full text-xs border border-slate-300 rounded bg-white px-1 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                        >
                          {group.members.map((m) => (
                            <option key={m.id} value={m.id}>{effectiveUnit(m) || "—"}</option>
                          ))}
                        </select>
                      ) : (
                        product.unit || "—"
                      )}
                    </td>
                    <td className="py-2 px-2 align-middle text-xs text-slate-600">
                      {product.estimated_weight || "—"}
                    </td>
                    <td className="py-2 px-2 align-middle text-xs text-slate-700 font-medium text-right">
                      {(() => {
                        const rank = storeData?.customer_rank || "a1";
                        const pm = gIsGroup ? priceBearingMember(group, rank) : product;
                        const price = pm.prices?.[rank];
                        return price ? `¥${price}` : "—";
                      })()}
                    </td>
                    <td className={`py-2 px-3 align-middle ${hasValue ? "bg-green-50" : ""}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600 font-medium w-12 shrink-0">
                          {effectiveUnit(product) || "—"}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={orderInfo.quantity}
                          onChange={(e) =>
                            handleQuantityChange(product.id, dailyUsageDate, e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp") {
                              e.preventDefault();
                              const nodeList = Array.from(
                                document.querySelectorAll('input[data-daily-input="1"]')
                              );
                              const nidx = nodeList.indexOf(e.target);
                              const next =
                                e.key === "ArrowUp"
                                  ? (nidx > 0 ? nodeList[nidx - 1] : null)
                                  : (nidx >= 0 && nidx < nodeList.length - 1 ? nodeList[nidx + 1] : null);
                              if (next) {
                                next.focus();
                                if (typeof next.select === "function") next.select();
                              }
                            }
                          }}
                          data-daily-input="1"
                          disabled={locked || orderInfo.ep_locked}
                          title={orderInfo.ep_locked ? "事前取込済みのため変更はお電話でお問い合わせください" : undefined}
                          data-testid={`daily-input-${product.id}`}
                          className={`flex-1 h-10 px-3 text-base border border-slate-300 text-right font-mono rounded focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 disabled:bg-slate-100 disabled:text-slate-400 ${hasValue ? "bg-white font-semibold text-emerald-700 border-emerald-300" : ""}`}
                          placeholder="0.00"
                        />
                      </div>
                      {hasValue && !locked && (() => {
                        const cellDelivery = orderInfo.delivery_date || selectedDate;
                        const isOverride = orderInfo.delivery_date && orderInfo.delivery_date !== selectedDate;
                        return (
                          <div className="flex items-center gap-1 mt-1.5 pl-14">
                            <span className="text-[11px] text-slate-500 shrink-0">📦 配送日:</span>
                            <select
                              value={cellDelivery}
                              onChange={(e) => handleCellDeliveryChange(product.id, dailyUsageDate, e.target.value)}
                              data-testid={`daily-cell-delivery-${product.id}`}
                              title="この商品の配送希望日（使用日と別の日に届けたい場合に変更）"
                              className={`flex-1 h-8 px-2 text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400 ${isOverride ? "border-amber-400 text-amber-700 font-semibold" : "border-slate-300 text-slate-600"}`}
                            >
                              {dailyDeliveryOptions.map((o) => (
                                <option key={o.date} value={o.date}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })()}
                      {!locked && (
                        <div className="mt-1.5 pl-14 space-y-1.5">
                          {(orderInfo.lines || []).map((ln, i) => {
                            const lnHas = ln.quantity && parseFloat(ln.quantity) > 0;
                            return (
                              <div key={i} className="flex items-center gap-1.5" data-testid={`daily-split-${product.id}-${i}`}>
                                <span className="shrink-0 text-xs font-bold text-emerald-700 w-5 text-center">{circledNum(i + 2)}</span>
                                <input
                                  type="number" step="0.01" min="0"
                                  value={ln.quantity}
                                  onChange={(e) => updateSplitLineQty(product.id, dailyUsageDate, i, e.target.value)}
                                  data-testid={`daily-split-input-${product.id}-${i}`}
                                  placeholder="0.00"
                                  className={`w-24 h-9 px-2 text-sm text-right font-mono border rounded focus:outline-none focus:ring-1 focus:ring-emerald-400 ${lnHas ? "border-emerald-300 text-emerald-700 font-semibold" : "border-slate-300"}`}
                                />
                                <select
                                  value={ln.delivery_date || ""}
                                  onChange={(e) => updateSplitLineDelivery(product.id, dailyUsageDate, i, e.target.value)}
                                  data-testid={`daily-split-delivery-${product.id}-${i}`}
                                  className="flex-1 h-8 px-1 text-[11px] border rounded bg-white border-slate-300 text-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                                >
                                  <option value="">配送日:主と同じ</option>
                                  {dailyDeliveryOptions.map((o) => (<option key={o.date} value={o.date}>{o.label}</option>))}
                                </select>
                                <button type="button" onClick={() => removeSplitLine(product.id, dailyUsageDate, i)} data-testid={`daily-split-remove-${product.id}-${i}`} className="shrink-0 h-8 w-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center text-sm">✕</button>
                              </div>
                            );
                          })}
                          {hasValue && (
                            <button
                              type="button"
                              onClick={() => addSplitLine(product.id, dailyUsageDate)}
                              data-testid={`daily-split-add-${product.id}`}
                              className="text-xs text-emerald-700 border border-dashed border-emerald-300 rounded px-2 py-1 hover:bg-emerald-50"
                            >
                              ＋分けて発注（用途違いで別明細に）
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {/* コメント行 */}
              <tr className="bg-amber-50 border-t-2 border-amber-200">
                <td colSpan="5" className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <label className="text-xs font-semibold text-amber-800 pt-2 whitespace-nowrap">
                      💬 連絡事項:
                    </label>
                    <textarea
                      value={dateComments[dailyUsageDate] || ""}
                      onChange={(e) => setDateComments(prev => ({...prev, [dailyUsageDate]: e.target.value}))}
                      placeholder="この日のコメント..."
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                      rows="2"
                    />
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className="flex items-stretch gap-3">
          <div className="w-1 bg-emerald-600 rounded-sm" />
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              発注入力
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "weekly" ? "週単位（月〜土）で一括入力できます" : "日付を選んで入力できます"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center w-full sm:w-auto">
          {/* モード切替（スマホは日ごと固定のため非表示） */}
          <div className="hidden md:inline-flex rounded-md border border-slate-200 overflow-hidden bg-white">
            <button
              data-testid="mode-weekly"
              onClick={() => setMode("weekly")}
              className={`h-10 px-4 text-sm flex items-center gap-1.5 transition-colors ${
                mode === "weekly"
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <CalendarDays className="h-4 w-4" />
              週間一括入力
            </button>
            <button
              data-testid="mode-daily"
              onClick={() => setMode("daily")}
              className={`h-10 px-4 text-sm flex items-center gap-1.5 transition-colors ${
                mode === "daily"
                  ? "bg-emerald-600 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <CalendarDays className="h-4 w-4" />
              日ごと入力
            </button>
          </div>

          {mode === "weekly" && sortedWeeks.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="week-select" className="text-sm font-medium text-slate-600">
                表示週:
              </label>
              <select
                id="week-select"
                data-testid="week-select"
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                {sortedWeeks.map((ws, index) => (
                  <option key={index} value={index}>
                    {weekLabel(ws)} ({ws[0]?.delivery_date} 〜 {ws[ws.length - 1]?.delivery_date})
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === "daily" && sortedSchedules.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">
                配送日:
              </label>
              <DeliveryDatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                schedules={sortedSchedules}
                testId="date-select"
              />
            </div>
          )}

          <Button
            onClick={handleSaveOrders}
            disabled={saving}
            data-testid="save-orders-button"
            className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-md h-10 px-5 shadow-sm w-full sm:w-auto ml-auto sm:ml-0"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "保存中..." : "発注を保存"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : (
        <div className="space-y-4">
          {/* 締め切りバナー */}
          {(() => {
            const firstDD =
              mode === "weekly"
                ? sortedWeeks[selectedWeek]?.[0]?.delivery_date
                : selectedDate;
            const info = getDeadlineForDeliveryDate(firstDD);
            if (!info) return null;
            const passed = new Date() > info.deadline;
            const JW = ["日", "月", "火", "水", "木", "金", "土"];
            const fmt = (dt) => `${dt.getMonth() + 1}月${dt.getDate()}日`;
            const dlWd = JW[info.deadline.getDay()];
            const dlTime =
              info.deadline.getMinutes() === 0
                ? `${info.deadline.getHours()}時`
                : `${info.deadline.getHours()}:${String(info.deadline.getMinutes()).padStart(2, "0")}`;
            return (
              <div
                data-testid="deadline-banner"
                className={`rounded-lg border px-4 py-3 flex items-start gap-2.5 text-sm ${
                  passed
                    ? "bg-slate-100 border-slate-300 text-slate-600"
                    : "bg-amber-50 border-amber-300 text-amber-900"
                }`}
              >
                <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                {passed ? (
                  <span>
                    <b>{fmt(info.monday)}週</b>納品分の受付は締め切りました（締め切り：
                    {fmt(info.deadline)}（{dlWd}）{dlTime}）。変更は「発注確認＆変更」からお願いします。
                  </span>
                ) : (
                  <span>
                    <b>{fmt(info.monday)}週</b>納品分の受付中です：締め切りは{" "}
                    <b>
                      {fmt(info.deadline)}（{dlWd}）{dlTime}
                    </b>
                    まで
                  </span>
                )}
              </div>
            );
          })()}

          {/* 検索 ＋ 発注グリッド（カテゴリ選択は本体サイドバーへ移動） */}
          <div className="space-y-3">
            {renderSearchBar()}
              {mode === "weekly" ? (
                displayedProducts.length === 0 ? (
                  renderNoProducts()
                ) : (
                  renderWeeklyTable()
                )
              ) : !selectedDate ? (
                <div className="text-center py-12 text-slate-500">配送日を選択してください</div>
              ) : !selectedSchedule ? (
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 text-center">
                  選択した日付（{selectedDate}）には配送スケジュールがありません。<br />
                  別の日付を選択してください。
                </div>
              ) : (
                <>
                  {isChangeLocked(selectedSchedule) ? (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      この日付は変更締切を過ぎています。変更する場合は「発注確認＆変更」ページから申請してください。
                    </div>
                  ) : isOrderDeadlinePassed(selectedSchedule) ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      この日付は発注締切を過ぎています。新規・変更は「発注確認＆変更」ページから申請してください。
                    </div>
                  ) : null}
                  {displayedProducts.length === 0 ? (
                    renderNoProducts()
                  ) : (
                    renderDailyTable()
                  )}
                </>
              )}
          </div>
        </div>
      )}
      <OrderPreviewDialog
        preview={orderPreview}
        saving={saving}
        onCancel={() => setOrderPreview(null)}
        onConfirm={submitOrders}
      />
    </div>
  );
}
